/**
 * `<IndicatorButton>` — the second glyph in the vertical strip, and the picker
 * behind it: what this chart can draw, what it already draws, and one number per
 * parameter before you commit.
 *
 * ## It adds to the *active* chart
 *
 * The button is mounted once for the whole strip and is handed the chart
 * `lib/active-section.ts` resolved — not the first chart in the layout. A
 * four-chart layout otherwise grows every indicator on the top-left pane, which
 * looks like the button is broken rather than aimed somewhere else.
 *
 * ## Nothing here decides where a plot goes
 *
 * Placement is a property of the indicator (`price` over the candles, `own` in a
 * pane of its own) and arrives with the catalog, so this renders a quiet marker
 * for the `own` ones and hands the entry to `lib/add-indicator`. The geometry —
 * pane, axis, alias, stroke token — is that module's, which is why it is
 * testable without a DOM and why this file mints ids and does nothing else with
 * them.
 *
 * ## Search covers the description, not just the name
 *
 * "convergence" has to find MACD, whose label says nothing of the kind. The
 * catalog carries a sentence per indicator for exactly this, and a picker that
 * matched labels alone would make sixteen entries feel like sixteen acronyms.
 *
 * ## Removing is not an afterthought
 *
 * Everything currently on the chart is listed at the top with a remove control.
 * Adding with no way to remove is a trap — the plot is workspace data, so it
 * comes back on every reload — and the list is also the only feedback that a
 * click landed, since the dialog deliberately stays open for the second and
 * third indicator.
 *
 * ## And neither is editing
 *
 * That same list is where an indicator already on the chart is **changed**: its
 * parameters (`macd_12_26_9` → `macd_5_35_5`) and each of its plots' type, width,
 * dash and colour. It is the only place that already enumerates what is on the
 * chart, so a second surface listing the same things would be the duplication
 * the applied list exists to avoid.
 *
 * Two rules the editor is shaped by:
 *
 * - **Parameters move as a family.** The alias encodes them, so all three of
 *   MACD's outputs are rewritten together by `lib/add-indicator`'s
 *   `editIndicatorParams` — half a family is two indicators in one pane, one of
 *   them mislabelled. Hence one Apply for the row, not one per plot.
 * - **Style is per plot, and is a repaint.** Colour, width and dash go through
 *   `setPlotStyle`/`setPlotPalette` and are excluded from `ChartGroupView`'s
 *   structural key, so dragging a width spinner does not tear down the WebGL
 *   surface and refetch. Plot *type* deliberately does not go that way: it is a
 *   different renderable class over a different data shape, so it reuses
 *   `setChartType` and rebuilds.
 * - **Reference lines are neither.** RSI's 30/70 and CCI's ±100 are the
 *   *catalog's*, synthesised into the render plan on every build and stored
 *   nowhere (D8); a line the user places is not derivable from anything, so it
 *   is stored, on `AxisInfo.levels`. The whole rule is that an axis carrying
 *   that array answers for all of what is drawn on it — the catalog's zero line
 *   included — and an absent array means the catalog's. See {@link AxisLevelsRow}.
 *
 * Colours are offered as **theme tokens**, never as hex (D13) — a picker that
 * wrote `#4fc3f7` would survive a theme switch unchanged, which is the one thing
 * token resolution exists to prevent.
 *
 * ## `position: fixed`, measured off the trigger
 *
 * The same reason `GroupBar`, `IntervalPicker`, `SymbolSearch` and
 * `ChartToolbar` are: an absolutely-positioned panel is clipped by the nearest
 * ancestor with `overflow: hidden`, and the strip sits inside one. That has now
 * bitten this codebase four times. It opens to the **left**, because the strip
 * is pinned between the chart and the screener and a panel to the right would
 * cover the grid.
 *
 * Colours are `var(--qf-*)` only.
 */

import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { createPortal } from "react-dom";
import type {
  AxisInfo,
  AxisLevel,
  ChartGroup,
  Pane,
  PlotPalette,
  PlotStyle,
  PlotType,
} from "@qufolio/model";

import { catalogLevels, loadIndicatorCatalog, type IndicatorEntry } from "../api/indicators";
import {
  addBandBetween,
  addIndicator,
  addSmoother,
  axisFor,
  QUAD_DEFAULTS,
  DEFAULT_SMOOTHER,
  editSmoother,
  appliedIndicators,
  bandCandidates,
  composedBands,
  editIndicatorParams,
  setIndicatorTimeframe,
  type Timeframe,
  addOutputBack,
  removedOutputs,
  removeIndicator,
  removeOutput,
  removePlot,
  type AppliedIndicator,
  type AppliedPlot,
  type BandCandidate,
  type IndicatorParams,
  addVolume,
  hasVolume,
  VOLUME_ENTRY,
} from "../lib/add-indicator";
import {
  paneScaleOf,
  type PaneScale,
  type PaneScaleMode,
} from "../lib/pane-scale";
import { canMovePane, movePane, movePaneTo } from "../lib/pane-order";
import {
  movePlotsToPane,
  NEW_PANE,
  plotsScale,
  joinScale,
  ownAxisOf,
  scalePartners,
  setPlotsScale,
  SMOOTH_KINDS,
  splitSmoothed,
  type PlotScale,
  type ScaleChoice,
  type SmoothKind,
} from "../lib/move-to-pane";
import { setAxisScale } from "../lib/pane-scale";
import { groupByCategory } from "../lib/indicator-groups";
import { GridToolbarButton, IndicatorIcon } from "./GridToolbarButton";
import { useTheme, useTokenResolver } from "./theme";
import { useDefaultCandleStyle, type CandleStyle } from "../lib/candle-style";
import { themeCssVariables } from "@qufolio/model";
import { ColorSelect, NO_COLOR } from "./ColorSelect";
import {
  DEFAULT_OPACITY,
  INSIDE_COLOR,
  OUTSIDE_COLOR,
  isBarHighlightAlias,
} from "../lib/inside-outside";

/** What a style edit changes on one plot. Any subset; absent keys are untouched. */
export interface PlotPatch {
  type?: PlotType;
  style?: PlotStyle;
  palette?: PlotPalette;
  /**
   * Show or hide the plot. Not part of `style`, because it is not one: a hidden
   * plot keeps its colour, its width and its loaded bars, and the renderer
   * applies it with a property write rather than a restyle.
   */
  hidden?: boolean;
}

/**
 * Plot types an indicator series can take.
 *
 * `candlestick`, `ohlc` and `band` are absent on purpose: the first two need
 * four numbers a bar and the third needs two, while an indicator output is one
 * number a bar. Offering them would produce an empty series that looks like a
 * broken chart rather than a wrong choice.
 */
const PLOT_TYPES: { id: PlotType; label: string }[] = [
  { id: "line", label: "Line" },
  { id: "mountain", label: "Mountain" },
  { id: "column", label: "Column" },
  { id: "histogram", label: "Histogram" },
  { id: "scatter", label: "Scatter" },
];

/** Named dash choices — the array they draw with is the renderer's business. */
const DASH_STYLES: { id: NonNullable<PlotStyle["dashStyle"]>; label: string }[] = [
  { id: "solid", label: "Solid" },
  { id: "dashed", label: "Dashed" },
  { id: "dotted", label: "Dotted" },
];

/**
 * How a plot is coloured, as the one choice a person makes.
 *
 * `single` is `style.stroke` and no palette; the other two are the contract's
 * paired tier (`upColor`/`downColor` + `colorMode`). They are one control rather
 * than two because "one colour or two" and "which two" are the same decision,
 * and a `colorMode` left behind by a switch back to one colour is exactly the
 * kind of stale key that makes a plot render as neither.
 */
const COLOR_MODES = [
  { id: "single", label: "One colour" },
  { id: "sign", label: "Sign ±" },
  { id: "delta", label: "Change Δ" },
  { id: "quad", label: "Change Δ ×4" },
] as const;

type ColorMode = (typeof COLOR_MODES)[number]["id"];

/**
 * Per-bar colouring is a PaletteProvider, and only the column series has one —
 * a line is a single stroke however its values move. So the mode control is
 * offered where it does something and hidden where it would be a lie.
 */
const SIGN_COLORABLE = new Set<PlotType>(["column", "histogram"]);

/**
 * What the shape select shows. The glyph leads so the list reads at a glance;
 * a signal's `triangle` points up for up bars and down for down bars, which is
 * why `triangleDown` is its own entry — a scatter has no direction to follow.
 */
type MarkerShape = NonNullable<PlotStyle["markerShape"]>;

/**
 * `DEFAULT_MARKER_SHAPE` in chart-engine's `plan/signal.ts`, restated rather
 * than imported: that barrel loads SciChart, which cannot load in this file's
 * tests. The owner's choice, and the builder draws it when a plot names none.
 */
const DEFAULT_MARKER_SHAPE: MarkerShape = "circle";

/**
 * The builder's default marker sizes (`SIGNAL_MARKER_PX` / `SCATTER_MARKER_PX`
 * in `scichart-builder.ts`), restated for the placeholder for the same reason:
 * the placeholder should show the size actually drawn when the box is blank.
 */
const SIGNAL_MARKER_PX = 7;
const SCATTER_MARKER_PX = 7;

const MARKER_SHAPE_LABELS: Record<MarkerShape, string> = {
  circle: "● Circle",
  triangle: "▲ Triangle",
  triangleDown: "▼ Triangle down",
  arrowUp: "🠵 Arrow up",
  arrowDown: "🠷 Arrow down",
  square: "■ Square",
  diamond: "◆ Diamond",
  cross: "✚ Cross",
  x: "✕ X",
  line: "▬ Line",
  column: "▮ Column",
  text: "T Text",
};

/** What a `text` marker reads until the user types something: a word under a bar. */
const DEFAULT_MARKER_TEXT = "TXT";
/** The contract's `markerText` maxLength. */
const MARKER_TEXT_MAX = 6;

/** The Record above is exhaustive over the contract's enum, so this is too. */
const MARKER_SHAPES = Object.keys(MARKER_SHAPE_LABELS) as MarkerShape[];

/**
 * A scatter's choices: every shape but `column`, which fills a signal's lane —
 * a scatter has no lane, and the builder would only draw it as a square.
 */
const SCATTER_SHAPES = MARKER_SHAPES.filter((shape) => shape !== "column");

const colorModeOf = (plot: AppliedPlot): ColorMode =>
  plot.palette?.upColor || plot.palette?.downColor
    ? plot.palette.colorMode === "delta"
      ? "delta"
      : plot.palette.colorMode === "quad"
        ? "quad"
        : "sign"
    : "single";

/**
 * The catalog, fetched at most once per page load — a few kilobytes describing
 * sixteen indicators, which does not change while the tab is open.
 *
 * An *empty* answer is deliberately not cached: it means qv-data is unreachable
 * or unconfigured, and a developer who starts it after the app should get a
 * filled picker on the next open rather than after a reload.
 */

export interface IndicatorButtonProps {
  /** The active chart — the one an indicator is added to. */
  group: ChartGroup;
  mintId: () => string;
  /** The group's new panes, computed here and stored verbatim by the reducer. */
  onPanes: (panes: Pane[]) => void;
  /**
   * Change one plot's type, style or palette.
   *
   * Optional, and the style controls appear only when it is supplied: a host
   * that cannot apply the change should not offer it. Deliberately *not* folded
   * into `onPanes` — style is a repaint and a whole-panes write is structural,
   * so routing a colour through it would cost a WebGL teardown and a refetch.
   */
  onPlot?: (plotId: string, patch: PlotPatch) => void;
  /**
   * Pin this indicator's pane to a range, or let it fit the window.
   *
   * Named for the **pane**, not the indicator, because that is what it is: the
   * range lives on the axis, and two indicators sharing a pane share it. Only
   * offered on an own-pane row for the same reason — an overlay draws on the
   * price axis, whose scale is the chart-level `priceFit` setting.
   */
  onScale?: (paneId: string, scale: PaneScale) => void;
  /**
   * Open the panel with one applied indicator already expanded.
   *
   * Nonce-keyed rather than a bare key, the same shape as the toolbar's
   * `goTo` date: asking for the same indicator twice has to mean twice, or a
   * second click on the same legend row after closing the panel would do
   * nothing. The key is an `AppliedIndicator.key`, which is what `editing`
   * already holds — so the caller needs `appliedIndicators` and nothing else.
   */
  editRequest?: { key: string; nonce: number };
  /**
   * The phone's form: the panel is a full-screen sheet with **On chart** and
   * **Add** as two tabs (owner, 2026-10-13; `docs/mockups/form-factors.html`),
   * and the trigger is the caller's — the bottom row's `fx` key.
   */
  sheet?: { trigger: (open: boolean, toggle: () => void) => React.ReactNode };
}

export function IndicatorButton({
  group,
  mintId,
  onPanes,
  onPlot,
  onScale,
  editRequest,
  sheet,
}: IndicatorButtonProps) {
  const [open, setOpen] = useState(false);
  const [catalog, setCatalog] = useState<readonly IndicatorEntry[]>([]);
  // Anchored to the wrapper, not the button: `GridToolbarButton` renders the
  // `<button>` and does not forward a ref, and the two have the same box.
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  // Keyed on the nonce alone: the key repeating is the normal case — the same
  // row clicked twice — and keying on it would make the second click silent.
  useEffect(() => {
    if (!editRequest) return;
    void loadIndicatorCatalog().then((rows) => alive.current && setCatalog(rows));
    setOpen(true);
  }, [editRequest?.nonce]);

  const toggle = () => {
    // Loaded on first open, never on mount: a session that never opens this
    // should not pay for it, and the dialog is usable while it is in flight.
    if (!open) void loadIndicatorCatalog().then((rows) => alive.current && setCatalog(rows));
    setOpen((v) => !v);
  };

  return (
    <div ref={anchorRef} style={{ position: "relative", display: "flex" }}>
      {sheet ? (
        sheet.trigger(open, toggle)
      ) : (
        <GridToolbarButton label="Indicators" active={open} onClick={toggle}>
          <IndicatorIcon />
        </GridToolbarButton>
      )}
      {open && (
        <IndicatorPicker
          anchorRef={anchorRef}
          catalog={catalog}
          group={group}
          mintId={mintId}
          onPanes={onPanes}
          onPlot={onPlot}
          onScale={onScale}
          initialEditing={editRequest?.key}
          editNonce={editRequest?.nonce}
          sheet={!!sheet}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}

export interface IndicatorPickerProps {
  /** The toolbar button. The panel measures off it and hands focus back to it. */
  anchorRef: React.RefObject<HTMLElement | null>;
  catalog: readonly IndicatorEntry[];
  group: ChartGroup;
  mintId: () => string;
  onPanes: (panes: Pane[]) => void;
  /** See {@link IndicatorButtonProps.onPlot}. Omit to hide the style controls. */
  onPlot?: (plotId: string, patch: PlotPatch) => void;
  /**
   * Pin this indicator's pane to a range, or let it fit the window.
   *
   * Named for the **pane**, not the indicator, because that is what it is: the
   * range lives on the axis, and two indicators sharing a pane share it. Only
   * offered on an own-pane row for the same reason — an overlay draws on the
   * price axis, whose scale is the chart-level `priceFit` setting.
   */
  onScale?: (paneId: string, scale: PaneScale) => void;
  /** Expand this applied row and scroll it into view — see {@link IndicatorButtonProps.editRequest}. */
  initialEditing?: string;
  /**
   * Changes on every gear click, so a click while the panel is already open
   * still opens its row — the key alone repeats when the same row is asked twice.
   */
  editNonce?: number;
  /** Full screen, On chart / Add as tabs — see {@link IndicatorButtonProps.sheet}. */
  sheet?: boolean;
  onClose: () => void;
}

export function IndicatorPicker({
  anchorRef,
  catalog,
  group,
  mintId,
  onPanes,
  onPlot,
  onScale,
  initialEditing,
  editNonce,
  sheet = false,
  onClose,
}: IndicatorPickerProps) {
  const [query, setQuery] = useState("");
  /** Which row the keyboard is on; `-1` once a query matches nothing. */
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  /** The open row's settings — while focus is in them, hover leaves the row open. */
  const paramsRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  const needle = query.trim().toLowerCase();
  // A removed Volume needs a way back (owner, 2026-10-19). Not a server family,
  // so appended here — last, so the highlighted first row stays the catalog's —
  // and only while the chart draws none and there is a catalog to sit in.
  const volumeOn = hasVolume(group);
  const listed = useMemo(
    () => (volumeOn || catalog.length === 0 ? catalog : [...catalog, VOLUME_ENTRY]),
    [catalog, volumeOn],
  );
  const matches = useMemo(
    () =>
      listed.filter(
        (entry) =>
          !needle ||
          entry.label.toLowerCase().includes(needle) ||
          entry.name.toLowerCase().includes(needle) ||
          // The description is why "convergence" finds MACD. Dropping it here
          // would make the box a spelling test for acronyms.
          entry.description.toLowerCase().includes(needle) ||
          // And the category (owner, 2026-11-02): "seas" lists the whole
          // Seasonality group, not only the rows that say the word.
          entry.category.toLowerCase().includes(needle),
      ),
    [listed, needle],
  );

  const applied = useMemo(() => appliedIndicators(group, catalog), [group, catalog]);
  const entry: IndicatorEntry | undefined = matches[active];

  /**
   * Which applied row is open for editing. One at a time — the panel is 420px
   * wide and every row expanded would bury the catalog it sits above.
   */
  const [editing, setEditing] = useState<string | null>(initialEditing ?? null);

  /**
   * The sheet's tab. Opens on what is on the chart when there is anything,
   * since editing is the more common visit; an empty chart opens on Add.
   * The desktop panel shows both at once and ignores this.
   */
  // Counted as indicators the user put there: the Price and Volume rows are
  // always listed (they are panes in the stack), and a chart showing only those
  // has nothing on it to edit.
  // Volume is removable now, but it is the chart's own and not an indicator added.
  const indicatorsOn = applied.filter((row) => row.removable && !isVolumeRow(row)).length;
  const [view, setView] = useState<"on" | "add">(() =>
    initialEditing || indicatorsOn > 0 ? "on" : "add",
  );
  const showOn = !sheet || view === "on";
  const showAdd = !sheet || view === "add";

  /**
   * The row a gear asked for, until it has been scrolled to.
   *
   * "On this chart" is capped at 38vh and scrolls inside itself, so on a chart
   * with many indicators the asked-for row could open below the fold — the
   * member saw the whole list and had to scroll to find the settings (support,
   * 2026-10-31). Held until the row exists: on a first open the catalog is
   * still loading and the applied rows are not all there yet.
   */
  const scrollTo = useRef<string | null>(initialEditing ?? null);
  const appliedScrollRef = useRef<HTMLDivElement | null>(null);

  // A gear clicked while the panel is already open: the panel stays mounted,
  // so `useState`'s initial value above never sees it.
  const firstNonce = useRef(editNonce);
  useEffect(() => {
    if (editNonce === firstNonce.current || !initialEditing) return;
    firstNonce.current = undefined;
    setEditing(initialEditing);
    setView("on");
    scrollTo.current = initialEditing;
  }, [editNonce]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * The pane being dragged by its grip, and the pane it is currently over.
   *
   * Held as **pane** ids rather than row keys because that is what moves: two
   * families can share a pane, and both of their rows are handles on the same
   * band. Reordering by row key would let the two disagree about where their
   * pane went.
   */
  const [drag, setDrag] = useState<{
    paneId: string;
    overPaneId: string | null;
    /**
     * The row under the pointer, which is *not* the same question as the pane.
     * A pane can own several rows — the price pane owns its own row plus every
     * overlay on it — and the drop line belongs on the one row being pointed at
     * rather than on all of them at once.
     */
    overKey: string | null;
  } | null>(null);

  const paneIndex = useCallback(
    (paneId: string) => group.panes.findIndex((pane) => pane.id === paneId),
    [group.panes],
  );

  /**
   * Drop `paneId` into the slot the pane under the pointer currently holds.
   *
   * "Onto" rather than "between": the rows are the panes, so the only
   * unambiguous target a pointer can name is one of them, and the indicator line
   * is drawn on the side the pane will arrive from.
   */
  const dropPane = useCallback(
    (paneId: string, overPaneId: string) => {
      const to = paneIndex(overPaneId);
      if (to < 0) return;
      const after = movePaneTo(group, paneId, to);
      if (after !== group) onPanes(after.panes);
    },
    [group, onPanes, paneIndex],
  );

  /**
   * Pointer events rather than HTML5 drag-and-drop, and no library for it.
   *
   * The app already drags four things this way — pane seams, layout tracks, the
   * detached dialog — so this is the idiom rather than a choice. Capture is what
   * keeps the drag alive once the pointer leaves the 12px grip, which it does
   * immediately.
   */
  const startPaneDrag = useCallback(
    (event: ReactPointerEvent<HTMLElement>, paneId: string) => {
      // preventDefault stops the text selection a drag across labels would
      // otherwise paint; stopPropagation keeps the popover's own outside-click
      // and the row's click handlers out of it.
      event.preventDefault();
      event.stopPropagation();

      const handle = event.currentTarget;
      handle.setPointerCapture(event.pointerId);
      // Mirrored outside React state as well as in it: `up` fires in the same
      // tick as the last `move`, and reading the state there would read the
      // value from the render that has not happened yet.
      let over: string | null = null;
      setDrag({ paneId, overPaneId: null, overKey: null });

      const move = (ev: PointerEvent) => {
        // `elementFromPoint` rather than measured row rects: the rows change
        // height when one is expanded, and a cached rect is wrong the moment
        // somebody opens the editor mid-drag. Capture does not affect it.
        const under = document.elementFromPoint(ev.clientX, ev.clientY);
        const row = under?.closest("[data-qf-pane-row]") as HTMLElement | null;
        const target = row?.dataset.qfPaneRow ?? null;
        // A row of the pane already being dragged is not a drop: it would be a
        // move onto itself, and lighting it up would promise one.
        over = target && target !== paneId ? target : null;
        setDrag({ paneId, overPaneId: over, overKey: over ? (row?.dataset.qfRowKey ?? null) : null });
      };

      const up = () => {
        handle.releasePointerCapture(event.pointerId);
        handle.removeEventListener("pointermove", move);
        handle.removeEventListener("pointerup", up);
        // One workspace write per drag, on release — the same rule the pane
        // dividers follow. A move per crossed row would mint a save each time.
        if (over) dropPane(paneId, over);
        setDrag(null);
      };

      handle.addEventListener("pointermove", move);
      handle.addEventListener("pointerup", up);
    },
    [dropPane],
  );

  /**
   * The keyboard half.
   *
   * Not a nicety: the grip is the only reorder control in this panel, and the
   * on-chart arrows are revealed by hovering, so without this the feature has no
   * pointer-free route at all.
   */
  const nudgePane = useCallback(
    (paneId: string, delta: -1 | 1) => {
      const after = movePane(group, paneId, delta);
      if (after !== group) onPanes(after.panes);
    },
    [group, onPanes],
  );

  /**
   * The parameters typed for the highlighted row, as text.
   *
   * Text and not numbers, so a half-deleted field is empty rather than `NaN` —
   * `resolveParams` puts the default back at the moment of adding, which is
   * what makes "type nothing at all" the working path.
   *
   * **Kept per entry, never reset when the highlight moves.** The highlight
   * follows the mouse, so it used to clear on every pass of the pointer: type 5
   * into SMA, cross another row on the way anywhere, and SMA went back to 20 —
   * and was then added at 20, twice (owner, 2026-10-03). Keyed by entry name,
   * a row's numbers come back with it and can never show under another's labels.
   */
  const [drafts, setDrafts] = useState<Record<string, Record<string, string>>>({});
  const draft = { values: (entry && drafts[entry.name]) || {} };
  const setParam = (param: string, value: string) => {
    if (!entry) return;
    const name = entry.name;
    setDrafts((all) => ({ ...all, [name]: { ...all[name], [param]: value } }));
  };

  /**
   * The typed parameters, each in the shape its own control produces.
   *
   * **Not everything is a number, and coercing everything was silently losing
   * two kinds.** `Number("ytd")` and `Number("SPY")` are both `NaN`, which this
   * mapped to `undefined`, which `resolveParams` reads as "use the default" —
   * so a dropdown or a symbol box could be changed, would show the change, and
   * would add the indicator with the value it started on. Nothing failed; the
   * control simply did not do anything.
   *
   * It cost the seasonality path's `from` and the cone's `years`, and it was
   * already costing every Symbol Indicator, which could only ever be added on
   * SPY. `bool` is unaffected — its checkbox writes `"1"` / `"0"` precisely so
   * the value survives an alias, and a number is what it should become.
   */
  const paramsOf = (): IndicatorParams => {
    const params: Record<string, number | string | undefined> = {};
    const kinds = new Map(entry?.params.map((p) => [p.name, p.kind]) ?? []);
    for (const [name, text] of Object.entries(draft.values)) {
      const kind = kinds.get(name);
      if (kind === "choice" || kind === "symbol") {
        params[name] = text.trim() || undefined;
        continue;
      }
      const value = Number(text);
      params[name] = text.trim() !== "" && Number.isFinite(value) ? value : undefined;
    }
    return params;
  };

  /**
   * Add, and stay open.
   *
   * A chart usually gets two or three indicators in one visit, and the list
   * above updates as each lands — closing would make the second one another
   * click on the toolbar with nothing to show for the first.
   */
  const add = (chosen: IndicatorEntry) => {
    if (chosen === VOLUME_ENTRY) {
      const next = addVolume(group);
      if (next !== group) onPanes(next.panes);
    } else onPanes(addIndicator(group, chosen, chosen === entry ? paramsOf() : {}, mintId).panes);
    // On the phone the list you added from hides what you added; go and show it.
    if (sheet) setView("on");
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      if (entry) add(entry);
      return;
    }
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    // A number input owns its own arrows — they step the value, which is the
    // whole reason it is a number input.
    const target = event.target as HTMLElement;
    if (target instanceof HTMLInputElement && target.type === "number") return;
    event.preventDefault();
    setActive((i) =>
      event.key === "ArrowDown"
        ? Math.min(i + 1, matches.length - 1)
        : Math.max(i - 1, 0),
    );
  };

  // Bring the asked-for row to the top of its list, so its editor opens in view.
  // The list's own scrollTop rather than `scrollIntoView`, which would also
  // scroll the page and the panel around it.
  useLayoutEffect(() => {
    const key = scrollTo.current;
    const box = appliedScrollRef.current;
    if (!key || !box || editing !== key) return;
    const el = Array.from(box.querySelectorAll<HTMLElement>("[data-qf-row-key]")).find(
      (node) => node.dataset.qfRowKey === key,
    );
    if (!el) return;
    box.scrollTop += el.getBoundingClientRect().top - box.getBoundingClientRect().top;
    scrollTo.current = null;
  }, [applied, editing, view]);

  // A keyboard-driven highlight has to drag the viewport with it, or ArrowDown
  // walks the selection out of sight. Found by attribute rather than by index,
  // because the list has category headings interleaved with the rows.
  useLayoutEffect(() => {
    const el = listRef.current?.querySelector('[aria-current="true"]') as HTMLElement | undefined;
    el?.scrollIntoView?.({ block: "nearest" });
  }, [active, needle]);

  // Indices stay flat: the keyboard walks `matches`, and headings are not
  // selectable, so a nested index would have to be flattened on every arrow press.
  const groups = useMemo(() => groupByCategory(matches), [matches]);

  const Frame = sheet ? SheetFrame : Popover;

  return (
    <Frame anchorRef={anchorRef} labelledBy={titleId} onClose={onClose} onKeyDown={onKeyDown}>
      <div style={HEADER}>
        <h2 id={titleId} style={TITLE}>
          {sheet ? `Indicators · ${group.symbol}` : "Indicators"}
        </h2>
        <button type="button" aria-label="Close" onClick={onClose} style={CLOSE}>
          ×
        </button>
      </div>

      {sheet && (
        <div role="tablist" style={SHEET_TABS}>
          {(
            [
              ["on", `On chart (${indicatorsOn})`],
              ["add", "Add"],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={view === id}
              onClick={() => setView(id)}
              style={sheetTab(view === id)}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {sheet && view === "on" && applied.length === 0 && (
        <div style={EMPTY}>Nothing on this chart yet. Add one from the Add tab.</div>
      )}

      {showAdd && (
      <input
        // Not on the phone: focusing would raise the keyboard over the list.
        autoFocus={!sheet}
        value={query}
        // Named for what it searches: three fields, so "convergence" and "macd"
        // both work and neither is a surprise.
        aria-label="Search indicators"
        placeholder="Name or description"
        autoComplete="off"
        spellCheck={false}
        onChange={(event) => {
          setQuery(event.target.value);
          // Back to the top on every edit: the row under the old index is a
          // different indicator now, and Enter must never add one nobody read.
          setActive(0);
        }}
        style={SEARCH}
      />
      )}

      {showOn && applied.length > 0 && (
        <div
          ref={sheet ? appliedScrollRef : undefined}
          style={sheet ? { ...SECTION, flex: "1 1 auto", overflowY: "auto" } : SECTION}
        >
          {!sheet && <div style={SECTION_LABEL}>On this chart</div>}
          {/* Capped and scrolled inside itself, header outside the scroll, so a
              chart with twenty plots leaves the catalog the rest of the dialog —
              it used to grow until the list you add from was a sliver
              (owner's call 2026-09-30: this open, Fill between collapsed). */}
          <div ref={sheet ? undefined : appliedScrollRef} style={sheet ? undefined : APPLIED_SCROLL}>
          {applied.map((row) => (
            <AppliedRow
              key={row.key}
              row={row}
              expanded={editing === row.key}
              // Only an own-pane row is a handle on a pane. An overlay draws
              // into the price pane and has none of its own to move — the same
              // line `onScale` draws, for the same reason.
              canMoveUp={canMovePane(group.panes, row.paneId, -1)}
              canMoveDown={canMovePane(group.panes, row.paneId, 1)}
              dragging={drag?.paneId === row.paneId}
              // Which side the line is drawn on says where the pane will land:
              // a pane travelling down arrives *below* the row it was dropped
              // on, because lifting it out shifts that row up one first.
              dropEdge={
                drag && drag.overKey === row.key
                  ? paneIndex(drag.paneId) < paneIndex(row.paneId)
                    ? "below"
                    : "above"
                  : null
              }
              onDragStart={(event) => startPaneDrag(event, row.paneId)}
              onNudge={(delta) => nudgePane(row.paneId, delta)}
              onToggle={() => setEditing((k) => (k === row.key ? null : row.key))}
              onParams={(params) => {
                const next = editIndicatorParams(group, row, params);
                if (next === group) return;
                onPanes(next.panes);
                // The key carries the parameters, so the edited row comes back
                // under a new one and the editor would fold shut. Follow it by
                // its first plot, which the edit does not move.
                const first = row.plots[0]?.plotId;
                const moved = appliedIndicators(next, catalog).find((r) =>
                  r.plots.some((p) => p.plotId === first),
                );
                if (moved) setEditing(moved.key);
              }}
              onTimeframe={
                isDailyChart(group)
                  ? (tf) => {
                      const next = setIndicatorTimeframe(group, row, tf);
                      if (next === group) return;
                      onPanes(next.panes);
                      // The key carries the timeframe; follow the row by its first plot.
                      const first = row.plots[0]?.plotId;
                      const moved = appliedIndicators(next, catalog).find((r) =>
                        r.plots.some((p) => p.plotId === first),
                      );
                      if (moved) setEditing(moved.key);
                    }
                  : undefined
              }
              onPlot={onPlot}
              onScale={onScale}
              // Through `onPanes` rather than a callback of its own, because
              // there is no action to add one to: `App` routes `setPanes` and
              // the four style actions, and a fifth prop would need a reducer
              // case, an endpoint and a column before it did anything more than
              // this does. The cost is that a level edit is a whole-panes write
              // — see the concerns in `AxisLevelsRow`.
              onLevels={(levels) => onPanes(withAxisLevels(group, row.paneId, levels, row.axis?.id).panes)}
              // The scale action edits a pane's *first* axis, so it is offered
              // only to the family on it — not to one moved in with Own scale,
              // whose second axis it would never reach.
              scaleIsPanes={
                row.axis?.id === undefined ||
                group.panes.find((p) => p.id === row.paneId)?.axes?.[0]?.id === row.axis.id
              }
              // Folded left over the row's targets: one pane id for an own-pane
              // family, one alias per plot for an overlay — the price pane stays
              // either way.
              onRemove={() => onPanes(row.targets.reduce(removeIndicator, group).panes)}
              onRemoveOutput={(plotId) => onPanes(removeOutput(group, plotId).panes)}
              onAddOutput={(name) => {
                const next = addOutputBack(group, row, name, mintId);
                if (next !== group) onPanes(next.panes);
              }}
              // Move to (owner-approved mock, 2026-10-03): only for a family
              // with a pane of its own that can go — volume moves through
              // *Volume on price* instead, and an overlay already shares one.
              moveTargets={
                row.removable &&
                !row.smoothOf &&
                !isVolumeRow(row) &&
                (row.placement === "own" ||
                  // A family the catalog puts in a pane of its own, moved onto
                  // the price pane on the shared scale, can still go back out —
                  // RS Rank was stranded there (owner, 2026-11-01).
                  row.entry?.placement === "own" ||
                  // An overlay drawn on a scale of its own — a Rank added to the
                  // price pane — can be taken back out, which the menu is for.
                  plotsScale(group, row.plots.map((p) => p.plotId)) !== "share")
                  ? group.panes
                      .map((pane, i) => ({ pane, i }))
                      .filter(({ pane }) => pane.id !== row.paneId)
                      .map(({ pane, i }) => ({
                        id: pane.id,
                        label:
                          pane.role === "price"
                            ? "Price pane"
                            : `Pane ${i + 1} · ${applied.find((r) => r.paneId === pane.id)?.label ?? pane.role}`,
                      }))
                  : undefined
              }
              // A new pane only when the row is not already alone in one —
              // otherwise it is the pane it is in.
              canSplit={
                row.placement === "price" ||
                applied.filter((r) => r.paneId === row.paneId && r.placement === "own").length > 1
              }
              onMove={(target, scale) => {
                const next = movePlotsToPane(
                  group,
                  {
                    plotIds: row.plots.map((p) => p.plotId),
                    target,
                    scale,
                    ...(row.entry ? { base: axisFor(row.entry, "base") } : {}),
                  },
                  mintId,
                );
                if (next !== group) onPanes(next.panes);
              }}
              // The scale in the pane it shares — the price pane, or one it was
              // moved into. Alone in a pane of its own there is nothing to share.
              paneScaleChoice={
                row.removable &&
                !row.smoothOf &&
                !isVolumeRow(row) &&
                // A box behind a candle has no scale to share (owner, 2026-10-13).
                !isBarHighlightAlias(row.plots[0]?.alias) &&
                (row.placement === "price" ||
                  applied.filter((r) => r.paneId === row.paneId).length > 1)
                  ? plotsScale(group, row.plots.map((p) => p.plotId))
                  : undefined
              }
              ownAxis={row.smoothOf ? undefined : ownAxisOf(group, row.plots.map((p) => p.plotId))}
              // Indicator on indicator (owner, 2026-10-06): an average of the
              // row's first line — not of the price row, and not of a smoother.
              onSmooth={
                !row.smoothOf &&
                !row.key.endsWith(":price") &&
                !isBarHighlightAlias(row.plots[0]?.alias) &&
                row.plots[0]
                  ? (kind, length) => {
                      const next = addSmoother(group, row.plots[0]!.plotId, kind, length, mintId);
                      if (next !== group) onPanes(next.panes);
                    }
                  : undefined
              }
              onEditSmoother={
                row.smoothOf && row.plots[0]
                  ? (kind, length) => {
                      const next = editSmoother(group, row.plots[0]!.plotId, kind, length);
                      if (next !== group) onPanes(next.panes);
                    }
                  : undefined
              }
              onOwnAxisScale={(axisId, scale) => {
                const next = setAxisScale(group, axisId, scale);
                if (next !== group) onPanes(next.panes);
              }}
              scalePartners={scalePartners(group, row.plots.map((p) => p.plotId)).map((partner) => ({
                axisId: partner.axisId,
                label:
                  applied.find((r) => r.plots.some((p) => partner.plotIds.includes(p.plotId)))?.label ??
                  "another indicator",
              }))}
              onPaneScaleChoice={(scale) => {
                const ids = row.plots.map((p) => p.plotId);
                const next = scale.startsWith("with:")
                  ? joinScale(group, ids, scale.slice("with:".length))
                  : setPlotsScale(
                      group,
                      ids,
                      scale as PlotScale,
                      mintId,
                      row.entry ? axisFor(row.entry, "base") : undefined,
                    );
                if (next !== group) onPanes(next.panes);
              }}
            />
          ))}
          </div>
        </div>
      )}

      {showOn && (
      <FillBetween
        group={group}
        catalog={catalog}
        mintId={mintId}
        onPanes={onPanes}
      />
      )}

      {showAdd && (
      <div ref={listRef} style={LIST}>
        {/* Grouped, not run-length encoded. Emitting a heading whenever the
            category changed assumed the catalog arrives sorted by it; it arrives
            in the server's table order, so "Momentum Indicators" appeared four
            separate times. `groupByCategory` also fixes the heading order, which
            first-appearance would have left with Momentum in fourth place. */}
        {groups.map((group) => (
          <div key={group.category}>
            <h3 style={CATEGORY}>{group.category}</h3>
            {group.items.map(({ entry: choice, index }) => {
          const isActive = index === active;
          return (
            <div
              key={choice.name}
              onMouseEnter={() => {
                // **Hover does not move off a row being edited** (owner,
                // 2026-11-02): a native dropdown's list closes with the pointer
                // over some other row, and following it collapsed the settings
                // just picked. Focus inside them pins the row; a click
                // elsewhere releases it.
                if (paramsRef.current?.contains(document.activeElement)) return;
                setActive(index);
              }}
            >
              <button
                type="button"
                aria-current={isActive ? "true" : undefined}
                // A locked family is shown and cannot be added. `disabled`
                // rather than a click that explains itself, because the row is
                // already carrying its reason in the title and a control that
                // looks live and refuses is worse than one that looks off.
                disabled={choice.locked === true}
                onClick={() => add(choice)}
                // **Both halves of the row are one-line and ellipsised**, and a
                // long name eats the description's width before its own runs
                // out — so on the widest entries the reader gets a truncated
                // name and no sentence at all. The panel is 420px and cannot
                // grow, and wrapping would make the list scroll for the sixteen
                // rows it is meant to be scanned across. A title is the one
                // affordance that costs the layout nothing.
                title={
                  choice.locked
                    ? `${choice.label} — not included in your plan`
                    : `${choice.label} — ${choice.description}`
                }
                style={rowStyle(isActive, choice.locked === true)}
              >
                <span style={ROW_LABEL}>{choice.label}</span>
                <span style={ROW_DESC}>{choice.description}</span>
                {choice.locked && <span style={LOCK_PILL}>locked</span>}
                {/* Quiet, and only on the ones that change the chart's shape:
                    a pane appearing under the candles should not be a surprise. */}
                {choice.placement === "own" && <span style={PILL}>own pane</span>}
                {/* **An event draws nothing continuous.** A signal output is a
                    strip of markers on the bars where something happened, not a
                    line you can trace — no value to hover, and a y position that
                    is a lane rather than a reading. That is different enough
                    from every other row here to be said before it is added
                    rather than discovered after. */}
                {isEventEntry(choice) && <span style={EVENT_PILL}>event</span>}
              </button>
              {isActive && (
                <div ref={paramsRef} style={PARAMS}>
                  {choice.params.map((param) => (
                    <label key={param.name} style={PARAM}>
                      {param.label}
                      {/* Four controls, chosen by the server's `kind` rather
                          than by guessing from the default's type — a symbol
                          and a choice are both strings and are not the same
                          control. `number` is the fallback so an older server
                          that sends no kind still renders what it always did. */}
                      {param.kind === "bool" ? (
                        // Written as 1 and 0 rather than as `true`/`false`,
                        // because the value's destination is an alias
                        // (`ceLong_22_3_1`) and `parse_alias` reads only
                        // numbers. The DSL spells the same argument `true`;
                        // `_Lowering.flag` takes either.
                        <input
                          type="checkbox"
                          aria-label={`${choice.label} ${param.label}`}
                          checked={
                            (draft.values[param.name] ?? String(param.default)) !== "0"
                          }
                          onChange={(event) =>
                            setParam(param.name, event.target.checked ? "1" : "0")
                          }
                        />
                      ) : param.kind === "choice" ? (
                        <select
                          aria-label={`${choice.label} ${param.label}`}
                          value={draft.values[param.name] ?? String(param.default)}
                          onChange={(event) => setParam(param.name, event.target.value)}
                          style={PARAM_INPUT}
                        >
                          {(param.choices ?? []).map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type={param.kind === "symbol" ? "text" : "number"}
                          // Named with the indicator, because several rows can
                          // carry a parameter called "Length".
                          aria-label={`${choice.label} ${param.label}`}
                          value={draft.values[param.name] ?? String(param.default)}
                          onChange={(event) =>
                            setParam(
                              param.name,
                              param.kind === "symbol"
                                ? event.target.value.toUpperCase()
                                : event.target.value,
                            )
                          }
                          style={PARAM_INPUT}
                        />
                      )}
                    </label>
                  ))}
                  <button
                    type="button"
                    aria-label={`Add ${choice.label}`}
                    onClick={() => add(choice)}
                    style={ADD}
                  >
                    Add
                  </button>
                </div>
              )}
            </div>
          );
            })}
          </div>
        ))}

        {matches.length === 0 && (
          <div style={EMPTY}>
            {catalog.length === 0
              ? "No indicator catalog — set VITE_QV_IND_URL to a running qv-data."
              : `No indicator matches “${query.trim()}”.`}
          </div>
        )}
      </div>
      )}
    </Frame>
  );
}

/**
 * The phone's frame: the whole screen, above the tab bar and everything else.
 * Same props as {@link Popover} so the picker can take either; it has no
 * anchor to measure and no outside to click.
 */
/** A daily chart — the only one a weekly or monthly indicator is drawn on. */
function isDailyChart(group: ChartGroup): boolean {
  const f = (group.frequency ?? "1d").toLowerCase();
  return f === "1d" || f === "d";
}

/** The Volume row in "on this chart" — removable, but the chart's own rather than an added indicator. */
function isVolumeRow(row: AppliedIndicator): boolean {
  return row.key.endsWith(":volume");
}

function SheetFrame({
  labelledBy,
  children,
  onKeyDown,
  onClose,
}: {
  anchorRef: React.RefObject<HTMLElement | null>;
  labelledBy: string;
  children: React.ReactNode;
  onKeyDown: (event: React.KeyboardEvent) => void;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  // Portalled to the body, carrying the theme itself (as the Move to menu
  // does): left inside the phone's bottom row, the row's `touch-action: none`
  // would stop this sheet scrolling and its drags would reach the row's swipe.
  const theme = useTheme();
  const themeVars = useMemo(() => themeCssVariables(theme) as React.CSSProperties, [theme]);
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={labelledBy}
      data-tour="indicator-picker"
      onKeyDown={onKeyDown}
      style={{
        ...themeVars,
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
        background: "var(--qf-surface)",
        color: "var(--qf-text)",
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
    >
      {children}
    </div>,
    document.body,
  );
}

const SHEET_TABS: React.CSSProperties = {
  display: "flex",
  gap: 6,
  padding: "0 0.7rem 0.5rem",
};

function sheetTab(on: boolean): React.CSSProperties {
  return {
    flex: 1,
    font: "inherit",
    fontSize: 13,
    padding: "7px 0",
    borderRadius: 6,
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: on ? "var(--qf-accent)" : "var(--qf-border)",
    background: "transparent",
    color: on ? "var(--qf-accent)" : "var(--qf-text-secondary)",
  };
}

/**
 * One indicator already on the chart: what it is, and — expanded — what it can
 * be changed to.
 *
 * A row, not a dialog: editing an indicator is a small adjustment made while
 * looking at the chart, and a modal over the chart would hide the thing being
 * adjusted.
 */
/**
 * `sma_20` → `"SMA 20"`. The label alone is ambiguous the moment a chart holds
 * two of the same family — two `SMA` rows with nothing to tell them apart is
 * the one thing a pair-picker cannot afford.
 */
function optionLabel(candidate: BandCandidate): string {
  const params = candidate.alias.split("_").slice(1).join(", ");
  return params ? `${candidate.label} ${params}` : candidate.label;
}

/**
 * Fill the area between two series that are already on the chart.
 *
 * The catalog declares a band for the five channel families, because a channel
 * *is* two edges. This is the other half, and the pairs worth having are ones
 * no catalog could enumerate — RSI against its own average, a fast moving
 * average against a slow one, MACD against its signal — because they are made
 * by putting two indicators in one pane rather than by adding one thing.
 *
 * **Offered per pane, and that is the whole constraint.** Two series can only
 * be filled between if they share a y axis; a fill between an RSI and a price
 * is a shape drawn across two units that means nothing. So the control appears
 * once per pane that holds two fillable series, and never spans them.
 *
 * The band takes a paired palette, so it changes colour where the two cross
 * rather than only changing width — which is the entire reason to draw RSI
 * against its average this way rather than as two lines.
 */
function FillBetween({
  group,
  catalog,
  mintId,
  onPanes,
}: {
  group: ChartGroup;
  catalog: readonly IndicatorEntry[];
  mintId: () => string;
  onPanes: (panes: Pane[]) => void;
}) {
  const candidates = useMemo(() => bandCandidates(group, catalog), [group, catalog]);
  const bands = useMemo(() => composedBands(group), [group]);
  /** The pair being composed, per pane. Transient (D8) — nothing persists a half-made choice. */
  const [picked, setPicked] = useState<Record<string, { a?: string; b?: string }>>({});
  /** Collapsed until asked for — view state (D8), so it starts closed every time. */
  const [open, setOpen] = useState(false);

  if (candidates.size === 0 && bands.length === 0) return null;

  const paneLabel = (paneId: string) => {
    const pane = group.panes.find((p) => p.id === paneId);
    if (!pane) return "";
    if (pane.role === "price") return "Price";
    // The family's own label, not the raw alias head — that spelled the price
    // action pane `PAUPBARS`, which is an identifier and not a name. Resolved
    // through the catalog for the same reason every other label here is: the
    // server is what knows what an indicator is called.
    const head = (pane.plots ?? []).find((p) => p.outputAlias)?.outputAlias?.split("_")[0];
    if (!head) return "";
    const entry = catalog.find((e) =>
      e.outputs.some((o) => o.name.toLowerCase() === head.toLowerCase()),
    );
    return entry?.label ?? head;
  };

  // **Collapsed by default** (owner's call 2026-09-30). With a fill row per
  // eligible pane it grew a line per indicator and pushed the catalog off the
  // dialog; the count in the header says what is inside without opening it.
  if (!open) {
    return (
      <div style={SECTION}>
        <button type="button" style={SECTION_TOGGLE} aria-expanded={false} onClick={() => setOpen(true)}>
          ▸ Fill between{bands.length > 0 ? ` · ${bands.length}` : ""}
        </button>
      </div>
    );
  }

  return (
    <div style={{ ...SECTION, ...FILL_SCROLL }}>
      <button type="button" style={SECTION_TOGGLE} aria-expanded onClick={() => setOpen(false)}>
        ▾ Fill between{bands.length > 0 ? ` · ${bands.length}` : ""}
      </button>

      {bands.map((band) => (
        <div key={band.plotId} style={APPLIED_ROW}>
          <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
            {band.upper} &amp; {band.lower}
          </span>
          <button
            type="button"
            style={REMOVE}
            aria-label={`Remove fill between ${band.upper} and ${band.lower}`}
            title="Remove"
            onClick={() => onPanes(removePlot(group, band.plotId).panes)}
          >
            ×
          </button>
        </div>
      ))}

      {[...candidates].map(([paneId, options]) => {
        const choice = picked[paneId] ?? {};
        const a = choice.a ?? options[0]!.alias;
        const b = choice.b ?? options.find((o) => o.alias !== a)?.alias ?? "";
        const set = (patch: { a?: string; b?: string }) =>
          setPicked((prev) => ({ ...prev, [paneId]: { a, b, ...patch } }));

        return (
          <div key={paneId} style={FILL_ROW}>
            {candidates.size > 1 && <span style={FILL_PANE}>{paneLabel(paneId)}</span>}
            <select
              style={SELECT}
              value={a}
              aria-label="First series"
              onChange={(event) => set({ a: event.target.value })}
            >
              {options.map((option) => (
                <option key={option.plotId} value={option.alias}>
                  {optionLabel(option)}
                </option>
              ))}
            </select>
            <span style={FILL_AND}>&amp;</span>
            <select
              style={SELECT}
              value={b}
              aria-label="Second series"
              onChange={(event) => set({ b: event.target.value })}
            >
              {options
                // A series cannot be filled against itself; offering it would be
                // a control whose only outcome is nothing happening.
                .filter((option) => option.alias !== a)
                .map((option) => (
                  <option key={option.plotId} value={option.alias}>
                    {optionLabel(option)}
                  </option>
                ))}
            </select>
            <button
              type="button"
              style={FILL_ADD}
              disabled={!b}
              onClick={() => onPanes(addBandBetween(group, a, b, mintId).panes)}
            >
              Fill
            </button>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Pin this indicator's pane, or let it fit the window.
 *
 * **Named for the pane rather than the indicator**, because that is what it
 * writes: the range lives on the axis, and an axis is shared by everything
 * drawn on it. Label it "RSI scale" and it starts lying the moment somebody
 * puts a second oscillator in the same pane.
 *
 * `Natural` is offered only where the catalog declares bounds — RSI, Stoch, ADX,
 * MFI and Williams %R have them; MACD and CCI genuinely do not, and a button
 * that would have to invent a range is worse than no button.
 *
 * The custom inputs commit on **blur or Enter**, not per keystroke: typing `50`
 * passes through `5`, and a range of 0–5 applied for one frame is a visible
 * jump on every digit.
 */
function PaneScaleRow({
  row,
  axis,
  label,
  onScale,
}: {
  row: AppliedIndicator;
  /** The axis it drives — the pane's, or the row's own inside a shared pane. */
  axis: AxisInfo | undefined;
  label: string;
  onScale: (scale: PaneScale) => void;
}) {
  const natural =
    row.entry?.axis?.min !== undefined && row.entry?.axis?.max !== undefined
      ? { min: row.entry.axis.min, max: row.entry.axis.max }
      : undefined;
  const current = paneScaleOf(axis, natural);
  const [draft, setDraft] = useState<{ min: string; max: string } | null>(null);

  const shown = draft ?? {
    min: String(current.range?.min ?? natural?.min ?? 0),
    max: String(current.range?.max ?? natural?.max ?? 100),
  };

  const commit = (next: { min: string; max: string }) => {
    const min = Number(next.min);
    const max = Number(next.max);
    // A blank or reversed range would collapse the axis and take its labels
    // with it, so it is simply not applied — the field keeps the text and the
    // chart keeps the range it had.
    if (!Number.isFinite(min) || !Number.isFinite(max) || min >= max) return;
    onScale({ mode: "custom", range: { min, max } });
  };

  const choices: { id: PaneScaleMode; label: string; title: string }[] = [
    { id: "auto", label: "Auto", title: "Fit the values in the window" },
    ...(natural
      ? [
          {
            id: "natural" as const,
            label: `${natural.min}–${natural.max}`,
            title: `This indicator's own range`,
          },
        ]
      : []),
    { id: "custom", label: "Custom", title: "A range you set" },
  ];

  return (
    <div style={SCALE_ROW}>
      <span style={SCALE_LABEL}>{label}</span>
      {choices.map((choice) => (
        <button
          key={choice.id}
          type="button"
          title={choice.title}
          aria-pressed={current.mode === choice.id}
          onClick={() => {
            if (choice.id === "auto") return onScale({ mode: "auto" });
            if (choice.id === "natural") {
              return onScale({ mode: "natural", range: natural! });
            }
            // Custom with nothing typed yet starts from whatever is on the axis,
            // so the first click never moves the chart.
            commit(shown);
          }}
          style={scaleChoiceStyle(current.mode === choice.id)}
        >
          {choice.label}
        </button>
      ))}
      {current.mode === "custom" && (
        <>
          <input
            type="number"
            aria-label={`${row.label} axis minimum`}
            value={shown.min}
            onChange={(e) => setDraft({ ...shown, min: e.target.value })}
            onBlur={() => draft && commit(draft)}
            onKeyDown={(e) => e.key === "Enter" && draft && commit(draft)}
            style={PARAM_INPUT}
          />
          <input
            type="number"
            aria-label={`${row.label} axis maximum`}
            value={shown.max}
            onChange={(e) => setDraft({ ...shown, max: e.target.value })}
            onBlur={() => draft && commit(draft)}
            onKeyDown={(e) => e.key === "Enter" && draft && commit(draft)}
            style={PARAM_INPUT}
          />
        </>
      )}
    </div>
  );
}

/**
 * The dash a hand-placed line arrives with.
 *
 * Chosen here rather than left absent, because absent is not neutral: it draws
 * solid, and every line already on the axis came from the catalog dotted — so a
 * line added beside RSI's 30 and 70 would arrive looking like a different kind
 * of object than the two it joins. The one exception on screen is a seeded zero
 * line, which is solid because that is how the catalog draws *it*.
 */
const NEW_LEVEL_DASH: AxisLevel["dash"] = "dotted";

/**
 * Commit on Enter, and **swallow the key**.
 *
 * The popover's own `onKeyDown` reads a bare Enter as "add the highlighted
 * indicator from the catalog below", so a level typed and confirmed would also
 * drop a second SMA on the chart. It cannot be narrowed there the way ↑/↓ are —
 * those are skipped for a number input, which is exactly what these fields are.
 */
function commitOnEnter(event: React.KeyboardEvent, commit: () => void) {
  if (event.key !== "Enter") return;
  event.preventDefault();
  event.stopPropagation();
  commit();
}

/**
 * Write a set of levels onto a pane's first axis — or clear it.
 *
 * `undefined` removes the key rather than storing an empty array, and that
 * distinction is the entire feature: absent means the catalog's levels and its
 * zero line, `[]` means the user deleted every line and wants none. The two look
 * different on screen and mean different things on the next load, so the
 * clearing path and the delete-the-last-row path must never collapse into one.
 *
 * The **first** axis, for `withPaneScale`'s reason: an indicator pane has one in
 * practice, and taking it positionally keeps a hand-built pane whose plots carry
 * no `axisRef` working. Returns the group's panes untouched by identity when
 * there is no axis to write to, so React's memoisation still sees no change.
 */
function withAxisLevels(
  group: ChartGroup,
  paneId: string,
  levels: AxisLevel[] | undefined,
  axisId?: string,
): ChartGroup {
  return {
    ...group,
    panes: group.panes.map((pane) => {
      if (pane.id !== paneId) return pane;
      const axes = pane.axes;
      if (!axes?.length) return pane;
      // The row's own axis — a family moved in with Own scale draws against a
      // second one (`lib/move-to-pane.ts`) — else the pane's first.
      const at = Math.max(0, axes.findIndex((a) => a.id === axisId));
      return { ...pane, axes: axes.map((a, i) => (i === at ? { ...a, levels } : a)) };
    }),
  };
}

/**
 * The horizontal lines on this indicator's axis: the catalog's, or the user's
 * own once they have touched one.
 *
 * **The list is always live, and that is what makes the seeding rule structural
 * rather than a special case.** While the axis stores nothing the rows are the
 * catalog's — read through {@link catalogLevels}, the same function the seed
 * comes from — so *every* edit path here (retype a value, rename one, restyle
 * one, delete one, add one) maps the shown array and writes the result whole.
 * The first edit therefore stores the catalog's current set with one change in
 * it, and nothing on screen moves at the moment it stops being inherited. That
 * is the promise the Pane scale control above makes for a range, kept for a set
 * of lines.
 *
 * **Default is the reset, and it clears rather than writes.** A "reset" that
 * stored the catalog's values would be a snapshot: correct on the day, and never
 * again — a convention corrected server-side would reach every chart except the
 * ones somebody had reset. So the button removes the array, and the lines go
 * back to being derived.
 *
 * Which of the two is lit is **derived from the axis**, never stored, for
 * `paneScaleOf`'s reason: a stored mode is a second source of truth, and here it
 * would be one the array's own presence already answers.
 *
 * Values and labels commit on **blur or Enter** and a nonsense one is simply not
 * applied — the field keeps the text and the chart keeps its line — which is the
 * scale control's rule, for the scale control's reason: a level typed towards
 * `70` passes through `7`, and a line at 7 drawn for one frame is a visible
 * twitch on every digit. Dash and colour commit on change, because each is one
 * discrete act with no half-typed state to pass through.
 *
 * **Every commit here currently costs a rebuild**, and that is not by choice: a
 * level travels as a whole-panes write, and `ChartGroupView`'s `structuralKey`
 * strips an axis's `autoRange` and `visibleRange` but keeps the rest of it — so
 * `levels` falls into the key through the same side door `annotations` and the
 * axis range were each taken out of. Committing on blur rather than per
 * keystroke is what keeps that to one teardown per decision instead of one per
 * digit. The fix belongs next door: drop `levels` from the key and mirror it as
 * its own effect calling `builder.refreshReferenceLines()`, exactly as the axis
 * range is mirrored by `axisRangesOf`.
 */
function AxisLevelsRow({
  row,
  onLevels,
}: {
  row: AppliedIndicator;
  onLevels: (levels: AxisLevel[] | undefined) => void;
}) {
  const resolveToken = useTokenResolver();
  const inherited = useMemo(() => catalogLevels(row.entry?.axis), [row.entry]);
  const stored = row.axis?.levels;
  /** Presence, never length — `[]` is an answer, not an empty state. */
  const mine = stored !== undefined;
  const shown = stored ?? inherited;

  /**
   * The one text field being typed in, if any.
   *
   * One rather than a map keyed by row, because only one can be mid-edit:
   * `blur` fires before the next field takes focus, so leaving a box commits it.
   * That also makes the index safe to hold — a row cannot be deleted out from
   * under a draft without the draft committing first.
   */
  const [draft, setDraft] = useState<{
    index: number;
    field: "value" | "label";
    text: string;
  } | null>(null);
  /** The value typed beside Add. Transient (D8) — a half-typed line is not one. */
  const [adding, setAdding] = useState("");

  const write = (next: AxisLevel[]) => {
    setDraft(null);
    onLevels(next);
  };

  const commitValue = (index: number, text: string) => {
    const value = Number(text);
    // Blank or not a number would be a line with no position, which the contract
    // does not have a shape for — so it is refused rather than coerced to zero,
    // a y value the user never typed and could not tell from a rejection.
    if (text.trim() === "" || !Number.isFinite(value)) return;
    write(shown.map((level, i) => (i === index ? { ...level, value } : level)));
  };

  const commitLabel = (index: number, text: string) => {
    const label = text.trim();
    write(
      shown.map((level, i) =>
        // Emptied means "draw it bare", which is an absent key rather than an
        // empty string — the renderer would otherwise lay out a label of nothing.
        i === index ? { ...level, label: label === "" ? undefined : label } : level,
      ),
    );
  };

  const add = () => {
    const value = Number(adding);
    if (adding.trim() === "" || !Number.isFinite(value)) return;
    setAdding("");
    write([...shown, { value, dash: NEW_LEVEL_DASH }]);
  };

  const textOf = (index: number, field: "value" | "label", value: string) =>
    draft && draft.index === index && draft.field === field ? draft.text : value;

  return (
    <div style={LEVELS_BLOCK}>
      <div style={SCALE_ROW}>
        <span style={SCALE_LABEL}>Levels</span>
        <button
          type="button"
          // Named for the row, like every other control in this editor: two
          // oscillators expanded would otherwise offer two buttons called
          // "Default", and the Pane scale row above already owns "Custom".
          aria-label={`${row.label} default levels`}
          aria-pressed={!mine}
          title="The lines this indicator declares — corrected server-side, never copied here"
          onClick={() => mine && onLevels(undefined)}
          style={scaleChoiceStyle(!mine)}
        >
          Default
        </button>
        <button
          type="button"
          aria-label={`${row.label} custom levels`}
          aria-pressed={mine}
          disabled={!mine && !row.entry}
          title={
            !mine && !row.entry
              ? "Waiting for the indicator catalog"
              : "Lines you set, replacing the ones this indicator declares"
          }
          // **Gated on the catalog having resolved**, because the seed comes
          // from it. `catalogLevels(undefined)` is `[]`, and `[]` is not an
          // empty state here — it is "I deleted them all". So a click landing
          // in the fetch window would silently store the one state a user can
          // otherwise only reach deliberately, on an indicator that has levels,
          // and the lines would vanish rather than becoming editable.
          //
          // Disabled rather than seeded-later: the panel renders before
          // `loadIndicatorCatalog` resolves and a button that means something
          // different for its first few hundred milliseconds is worse than one
          // that is briefly unavailable and says why.
          onClick={() => !mine && row.entry && onLevels(inherited)}
          style={scaleChoiceStyle(mine)}
        >
          Custom
        </button>
        <input
          type="number"
          aria-label={`${row.label} new level`}
          placeholder="value"
          title="A y value on this axis"
          value={adding}
          onChange={(e) => setAdding(e.target.value)}
          onKeyDown={(e) => commitOnEnter(e, add)}
          style={{ ...PARAM_INPUT, marginLeft: "auto" }}
        />
        <button
          type="button"
          aria-label={`Add ${row.label} level`}
          onClick={add}
          style={FILL_ADD}
        >
          Add
        </button>
      </div>

      {shown.map((level, index) => (
        // Keyed by position, which is what the contract stores — `AxisLevel`
        // carries no id, deliberately, because nothing references one line.
        <div key={index}>
        <div style={LEVEL_ROW}>
          <input
            type="number"
            aria-label={`${row.label} level ${index + 1} value`}
            value={textOf(index, "value", String(level.value))}
            onChange={(e) => setDraft({ index, field: "value", text: e.target.value })}
            onBlur={() => draft && commitValue(draft.index, draft.text)}
            onKeyDown={(e) => commitOnEnter(e, () => draft && commitValue(draft.index, draft.text))}
            style={PARAM_INPUT}
          />
          <input
            type="text"
            aria-label={`${row.label} level ${index + 1} label`}
            placeholder="label"
            value={textOf(index, "label", level.label ?? "")}
            onChange={(e) => setDraft({ index, field: "label", text: e.target.value })}
            onBlur={() => draft && commitLabel(draft.index, draft.text)}
            onKeyDown={(e) => commitOnEnter(e, () => draft && commitLabel(draft.index, draft.text))}
            style={LEVEL_LABEL_INPUT}
          />
          <select
            aria-label={`${row.label} level ${index + 1} dash`}
            // An absent dash draws solid, so "Solid" is what it honestly reads
            // as; picking it writes the name rather than leaving the gap.
            value={level.dash ?? "solid"}
            onChange={(e) =>
              write(
                shown.map((l, i) =>
                  i === index ? { ...l, dash: e.target.value as AxisLevel["dash"] } : l,
                ),
              )
            }
            style={SELECT}
          >
            {DASH_STYLES.map((d) => (
              <option key={d.id} value={d.id}>
                {d.label}
              </option>
            ))}
          </select>
          <ColorSelect
            label={`${row.label} level ${index + 1} colour`}
            // "None" is a real answer here rather than an invisible line: the
            // render plan falls back to the muted text colour every catalog
            // level draws with (2026-11-02; it was the fainter axis line).
            allowNone
            value={level.color ?? NO_COLOR}
            resolve={resolveToken}
            onChange={(token) =>
              write(
                shown.map((l, i) =>
                  i === index ? { ...l, color: token === NO_COLOR ? undefined : token } : l,
                ),
              )
            }
          />
          <button
            type="button"
            aria-label={`Remove ${row.label} level ${index + 1}`}
            title="Remove"
            onClick={() => write(shown.filter((_, i) => i !== index))}
            style={REMOVE}
          >
            ×
          </button>
        </div>
        <LevelFillRow
          label={`${row.label} level ${index + 1}`}
          fill={level.fill}
          resolve={resolveToken}
          onFill={(fill) =>
            write(
              shown.map((l, i) => {
                if (i !== index) return l;
                const { fill: _old, ...rest } = l;
                return fill ? { ...rest, fill } : rest;
              }),
            )
          }
        />
        </div>
      ))}

      {mine && shown.length === 0 && (
        <div style={NOTE}>
          No lines on this axis. <em>Default</em> brings this indicator&rsquo;s own back.
        </div>
      )}
    </div>
  );
}

/** The opacities a level fill offers (owner's mock, 2026-11-02). */
const FILL_OPACITIES = [0.1, 0.2, 0.3, 0.5] as const;
/**
 * What an absent opacity draws — chart-engine's `LEVEL_FILL_OPACITY`, restated
 * because importing that barrel pulls SciChart into this component's tests.
 */
const LEVEL_FILL_OPACITY = 0.3;

/**
 * A level's second line: **Fill** None · Above · Below, a colour and an
 * opacity (`AxisLevel.fill`; `docs/level-fill-mock.html`). The colour and
 * opacity are greyed while None. An absent colour shows the default the chart
 * draws — Positive above, Negative below — and an absent opacity 30%.
 */
function LevelFillRow({
  label,
  fill,
  resolve,
  onFill,
}: {
  label: string;
  fill: AxisLevel["fill"];
  resolve: (token: string) => string;
  onFill: (fill: AxisLevel["fill"]) => void;
}) {
  const side = fill?.side;
  const defaultColor = side === "below" ? "negative" : "positive";
  const opacity = fill?.opacity ?? LEVEL_FILL_OPACITY;
  return (
    <div style={LEVEL_FILL_ROW}>
      <span style={SCALE_LABEL}>Fill</span>
      <span role="radiogroup" aria-label={`${label} fill`} style={{ display: "inline-flex", gap: 2 }}>
        {(["none", "above", "below"] as const).map((choice) => {
          const on = (side ?? "none") === choice;
          return (
            <button
              key={choice}
              type="button"
              role="radio"
              aria-checked={on}
              onClick={() =>
                choice === "none"
                  ? onFill(undefined)
                  : onFill({ ...(fill ?? {}), side: choice })
              }
              style={scaleChoiceStyle(on)}
            >
              {choice === "none" ? "None" : choice === "above" ? "Above" : "Below"}
            </button>
          );
        })}
      </span>
      <span style={side ? undefined : { opacity: 0.35, pointerEvents: "none" }}>
        <ColorSelect
          label={`${label} fill colour`}
          value={fill?.color ?? defaultColor}
          resolve={resolve}
          onChange={(token) => fill && onFill({ ...fill, color: token })}
        />
      </span>
      <select
        aria-label={`${label} fill opacity`}
        disabled={!side}
        value={String(opacity)}
        onChange={(e) => fill && onFill({ ...fill, opacity: Number(e.target.value) })}
        style={{ ...SELECT, ...(side ? {} : { opacity: 0.35 }) }}
      >
        {FILL_OPACITIES.map((o) => (
          <option key={o} value={String(o)}>
            {Math.round(o * 100)}%
          </option>
        ))}
      </select>
    </div>
  );
}

/**
 * Move an indicator's lines into another pane, or out into a new one
 * (`lib/move-to-pane.ts`; owner-approved mock, 2026-10-03).
 *
 * The lines share the target pane's scale unless **Own scale** is ticked — the
 * right default for "% Above MA" beside "% Above MA", where every line is 0–100.
 * Own scale is ignored for *New pane*, which always has its own.
 */
function MoveToMenu({
  label,
  targets,
  canSplit,
  onMove,
}: {
  label: string;
  targets: { id: string; label: string }[];
  canSplit: boolean;
  onMove: (target: string, scale: PlotScale) => void;
}) {
  const [open, setOpen] = useState(false);
  const [scale, setScale] = useState<PlotScale>("share");
  // The menu is portalled to `document.body`, outside the shell root that
  // carries the `--qf-*` variables — so every `var(--qf-surface)` in its style
  // resolved to nothing and it drew with **no background**, the controls behind
  // showing through (owner's screenshot, 2026-10-05). It carries them itself.
  const theme = useTheme();
  const themeVars = useMemo(() => themeCssVariables(theme) as React.CSSProperties, [theme]);
  const [at, setAt] = useState<{ top?: number; bottom?: number; right: number } | null>(null);
  const button = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);

  /**
   * **Portalled to the body, fixed to the button.** The row sits inside the
   * applied list, which scrolls inside itself — an absolutely placed menu was
   * clipped by it (owner's screenshot, 2026-10-03). It opens below the button,
   * or above when the window has no room below.
   */
  const place = () => {
    const r = button.current?.getBoundingClientRect();
    if (!r) return;
    const right = Math.max(8, window.innerWidth - r.right);
    const room = window.innerHeight - r.bottom;
    setAt(room < 220 && r.top > room
      ? { bottom: window.innerHeight - r.top + 2, right }
      : { top: r.bottom + 2, right });
  };

  // Closes on a press outside the button and the menu, and on any scroll or
  // resize — a fixed menu would otherwise float away from the row it belongs to.
  useEffect(() => {
    if (!open) return;
    const away = (event: PointerEvent) => {
      const t = event.target as Node;
      if (!button.current?.contains(t) && !menu.current?.contains(t)) setOpen(false);
    };
    const close = (event: Event) => {
      if (!menu.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", away);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("pointerdown", away);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  const pick = (target: string) => {
    onMove(target, scale);
    setOpen(false);
  };

  return (
    <div style={{ flex: "0 0 auto" }}>
      <button
        ref={button}
        type="button"
        aria-label={`Move ${label} to another pane`}
        aria-haspopup="menu"
        aria-expanded={open}
        title="Move to"
        onClick={() => {
          if (!open) place();
          setOpen((o) => !o);
        }}
        style={{ ...REMOVE, fontSize: 11 }}
      >
        Move to ▾
      </button>
      {open &&
        createPortal(
          <div
            ref={menu}
            role="menu"
            data-qf-picker-layer=""
            style={{ ...themeVars, ...MOVE_MENU, ...(at ?? { top: 0, right: 0 }) }}
          >
            {targets.map((t) => (
              <button key={t.id} type="button" role="menuitem" style={MOVE_ITEM} onClick={() => pick(t.id)}>
                {t.label}
              </button>
            ))}
            {canSplit && (
              <button type="button" role="menuitem" style={MOVE_ITEM} onClick={() => pick(NEW_PANE)}>
                New pane
              </button>
            )}
            {/* Owner, 2026-10-05: four ways, where the checkbox said two — a Rank
                in the price pane wants its own scale and no axis at all. */}
            <div role="radiogroup" aria-label="Scale" style={MOVE_CHECK}>
              <span>Scale</span>
              {PANE_SCALES.map((choice) => (
                <label key={choice.id} title={choice.title} style={MOVE_RADIO}>
                  <input
                    type="radio"
                    name={`move-scale-${label}`}
                    checked={scale === choice.id}
                    onChange={() => setScale(choice.id)}
                  />
                  {choice.label}
                </label>
              ))}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

const MOVE_MENU: React.CSSProperties = {
  position: "fixed",
  // Above the indicator dialog, which is itself a floating layer.
  zIndex: 10000,
  maxHeight: "60vh",
  overflowY: "auto",
  minWidth: 200,
  display: "flex",
  flexDirection: "column",
  padding: 4,
  background: "var(--qf-surface)",
  border: "1px solid var(--qf-border)",
  borderRadius: 6,
  boxShadow: "0 4px 14px rgba(0,0,0,0.35)",
};

const MOVE_ITEM: React.CSSProperties = {
  font: "inherit",
  fontSize: 12,
  textAlign: "left",
  padding: "5px 8px",
  border: 0,
  borderRadius: 4,
  background: "transparent",
  color: "var(--qf-text)",
  cursor: "pointer",
  whiteSpace: "nowrap",
};

/**
 * "Smooth [SMA▾] [20] [Add]" — a moving average of the row's line, or (with
 * `initial`) a smoother's own kind and length, applied on the button rather
 * than per keystroke: typing 50 passes through 5, and each would be a fetch.
 */
function SmoothRow({
  label,
  action,
  initial,
  onSubmit,
}: {
  label: string;
  action: "Add" | "Apply";
  initial?: { kind: SmoothKind; length: number };
  onSubmit: (kind: SmoothKind, length: number) => void;
}) {
  const [kind, setKind] = useState<SmoothKind>(initial?.kind ?? DEFAULT_SMOOTHER.kind);
  const [length, setLength] = useState(String(initial?.length ?? DEFAULT_SMOOTHER.length));
  const n = Number(length);
  const valid = Number.isInteger(n) && n >= 2 && n <= 500;
  return (
    <div style={SCALE_ROW}>
      <span style={SCALE_LABEL}>Smooth</span>
      <select
        aria-label={`${label} smoother`}
        value={kind}
        onChange={(e) => setKind(e.target.value as SmoothKind)}
        style={SELECT}
      >
        {SMOOTH_KINDS.map((k) => (
          <option key={k} value={k}>
            {k.toUpperCase()}
          </option>
        ))}
      </select>
      <input
        type="number"
        min={2}
        max={500}
        aria-label={`${label} smoother length`}
        value={length}
        onChange={(e) => setLength(e.target.value)}
        style={{ ...SELECT, width: 56 }}
      />
      <button type="button" disabled={!valid} onClick={() => onSubmit(kind, n)} style={REMOVE}>
        {action}
      </button>
    </div>
  );
}

/** The four scales, in the order the menu and the ⚙ select offer them. */
const PANE_SCALES: { id: PlotScale; label: string; title: string }[] = [
  { id: "share", label: "Share the pane's", title: "Draw on the scale already in the pane" },
  { id: "hidden", label: "Hidden", title: "Its own scale, with no axis drawn" },
  { id: "left", label: "Left", title: "Its own scale, with an axis on the left" },
  { id: "right", label: "Right", title: "Its own scale, with an axis on the right" },
];

const MOVE_RADIO: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  paddingLeft: 8,
  cursor: "pointer",
};

const MOVE_CHECK: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: 6,
  fontSize: 12,
  padding: "5px 8px",
  marginTop: 2,
  borderTop: "1px solid var(--qf-border)",
  color: "var(--qf-text-secondary)",
  cursor: "pointer",
};

function AppliedRow({
  row,
  expanded,
  canMoveUp,
  canMoveDown,
  dragging,
  dropEdge,
  onDragStart,
  onNudge,
  onToggle,
  onParams,
  onTimeframe,
  onPlot,
  onScale,
  onLevels,
  onRemove,
  onRemoveOutput,
  onAddOutput,
  moveTargets,
  canSplit = false,
  onMove,
  paneScaleChoice,
  onPaneScaleChoice,
  scalePartners = [],
  onSmooth,
  onEditSmoother,
  ownAxis,
  onOwnAxisScale,
  scaleIsPanes = true,
}: {
  row: AppliedIndicator;
  expanded: boolean;
  /** Whether the pane this row is a handle on has anywhere to go. */
  canMoveUp: boolean;
  canMoveDown: boolean;
  /** This row's pane is the one being dragged. */
  dragging: boolean;
  /** Draw the drop line, and on which edge — the side the pane will arrive on. */
  dropEdge: "above" | "below" | null;
  onDragStart: (event: ReactPointerEvent<HTMLElement>) => void;
  onNudge: (delta: -1 | 1) => void;
  onToggle: () => void;
  onParams: (params: IndicatorParams) => void;
  /** Draw this row weekly or monthly on a daily chart; absent off a daily chart. */
  onTimeframe?: (tf: Timeframe | undefined) => void;
  onPlot?: (plotId: string, patch: PlotPatch) => void;
  /**
   * Pin this indicator's pane to a range, or let it fit the window.
   *
   * Named for the **pane**, not the indicator, because that is what it is: the
   * range lives on the axis, and two indicators sharing a pane share it. Only
   * offered on an own-pane row for the same reason — an overlay draws on the
   * price axis, whose scale is the chart-level `priceFit` setting.
   */
  onScale?: (paneId: string, scale: PaneScale) => void;
  /**
   * Replace this axis's reference lines, or clear them back to the catalog's.
   *
   * Not an action of its own the way `onScale` is: a level has no endpoint in
   * qv-data's granular set, so it travels the whole-panes route the rest of this
   * panel already uses for geometry. Which makes it the one control here whose
   * cost is a rebuild rather than a repaint — see the note in the picker.
   */
  onLevels: (levels: AxisLevel[] | undefined) => void;
  onRemove: () => void;
  /** Delete one line of the family (owner, 2026-10-06). */
  onRemoveOutput?: (plotId: string) => void;
  /** Put a deleted line back, by catalog output name. */
  onAddOutput?: (outputName: string) => void;
  /** The panes this row's lines can move into; absent where Move to does not apply. */
  moveTargets?: { id: string; label: string }[];
  /** Whether *New pane* is offered — only when the row shares its pane. */
  canSplit?: boolean;
  onMove?: (target: string, scale: PlotScale) => void;
  /** The scale in a shared pane, when the row is in one; absent hides the control. */
  paneScaleChoice?: ScaleChoice;
  onPaneScaleChoice?: (scale: ScaleChoice) => void;
  /** Other indicators' scales in the same pane, offered as "With …". */
  scalePartners?: { axisId: string; label: string }[];
  /** The row's own axis inside a shared pane, when it has one — its range is editable. */
  ownAxis?: AxisInfo;
  /** Add a moving average of this row's line; absent where it cannot have one. */
  onSmooth?: (kind: SmoothKind, length: number) => void;
  /** For a smoother's row: change its kind or length. */
  onEditSmoother?: (kind: SmoothKind, length: number) => void;
  onOwnAxisScale?: (axisId: string, scale: PaneScale) => void;
  /** Whether this row's axis is its pane's first — the only one the scale control reaches. */
  scaleIsPanes?: boolean;
}) {
  /**
   * The parameters typed here, as text — the same reason the add form holds
   * text: a half-deleted field is empty rather than `NaN`, and `resolveParams`
   * puts the default back at the moment of applying.
   *
   * Seeded from the aliases actually on the chart, so opening the editor shows
   * what is drawn rather than the catalog's defaults.
   */
  const [draft, setDraft] = useState<Record<string, string>>({});
  const entry = row.entry;
  const removed = removedOutputs(row);

  /**
   * Parameters are edited behind an Apply, unlike style.
   *
   * Every keystroke would otherwise re-mint the alias — `macd_1_26_9` on the way
   * to `macd_12_26_9` — and each intermediate is a data request for an indicator
   * nobody asked for. Style has no such cost: it repaints what is already loaded.
   */
  const apply = () => {
    if (!entry) return;
    const params: Record<string, number | string | undefined> = {};
    entry.params.forEach((spec, i) => {
      const text = draft[spec.name] ?? String(row.params[i] ?? spec.default);
      if (spec.kind === "symbol" || spec.kind === "choice") {
        // Left as text: `resolveParams` owns the blank-falls-back-to-default
        // rule and the uppercasing, so doing either here would be a second
        // place for them to disagree.
        params[spec.name] = text.trim() || undefined;
        return;
      }
      const value = Number(text);
      params[spec.name] = text.trim() !== "" && Number.isFinite(value) ? value : undefined;
    });
    onParams(params);
    setDraft({});
  };

  // **Every row is a drop target; only some carry a grip.** An overlay has no
  // pane of its own to *move*, but it sits in one — so dropping onto "EMA 20"
  // means the price pane, which is the forgiving reading and the only one a
  // pointer can express. Restricting the target to gripped rows made the price
  // pane unreachable on any chart whose overlays outnumbered its own row.
  //
  // And a grip only where there is somewhere to go: a chart of one pane has a
  // price row like any other, and a grip on it would be furniture that could
  // never do anything.
  const movable = row.placement === "own" && (canMoveUp || canMoveDown);

  return (
    <div
      data-qf-pane-row={row.paneId}
      data-qf-row-key={row.key}
      style={{
        position: "relative",
        // The dragged row dims rather than being lifted out: the list is short
        // and removing it would make everything below jump by a row height,
        // which reads as the drop having already happened.
        opacity: dragging ? 0.45 : 1,
      }}
    >
      {dropEdge && (
        <div
          aria-hidden
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            [dropEdge === "above" ? "top" : "bottom"]: -1,
            height: 2,
            background: "var(--qf-accent)",
            borderRadius: 1,
            pointerEvents: "none",
          }}
        />
      )}
      <div style={APPLIED_ROW}>
        {movable && (
          <GripHandle
            label={row.label}
            canMoveUp={canMoveUp}
            canMoveDown={canMoveDown}
            onDragStart={onDragStart}
            onNudge={onNudge}
          />
        )}
        {/* A smoother sits under its source, indented and arrowed. */}
        <span style={row.smoothOf ? { ...APPLIED_LABEL, paddingLeft: 12 } : APPLIED_LABEL}>
          {row.smoothOf ? `↳ ${row.label}` : row.label}
        </span>
        {row.placement === "own" && <span style={PILL}>own pane</span>}
        {moveTargets && onMove && (moveTargets.length > 0 || canSplit) && (
          <MoveToMenu label={row.label} targets={moveTargets} canSplit={canSplit} onMove={onMove} />
        )}
        <button
          type="button"
          aria-label={`Edit ${row.label}`}
          aria-expanded={expanded}
          title="Edit"
          onClick={onToggle}
          style={REMOVE}
        >
          ⚙
        </button>
        {/* Price has no ×. Volume has one since 2026-10-19 (owner) and comes
            back from Indicators › Volume; where its bars sit is still
            *Volume on price* in right-click → Settings. */}
        {row.removable && (
          <button
            type="button"
            aria-label={`Remove ${row.label}`}
            title="Remove"
            onClick={onRemove}
            style={REMOVE}
          >
            ×
          </button>
        )}
      </div>

      {expanded && (
        <div style={EDITOR}>
          {/* Parameters are the family's, so one row and one Apply — rewriting
              half of MACD's aliases would draw two indicators in one pane.
              **First in the editor** (owner, 2026-10-08): below Smooth, scale
              and levels they fell under the fold, and a breadth row's List
              read as missing. */}
          {entry && entry.params.length > 0 && (
            <div style={PARAMS}>
              {entry.params.map((spec, i) => {
                const current = draft[spec.name] ?? String(row.params[i] ?? spec.default);
                const set = (value: string) =>
                  setDraft((d) => ({ ...d, [spec.name]: value }));
                return (
                  <label key={spec.name} style={PARAM}>
                    {spec.label}
                    {/* **The same four controls the add row offers**, and they
                        were missing here entirely: this rendered `type="number"`
                        for every parameter, so a dropdown became a spinner with
                        the choice sitting in it as text. A Symbol Indicator's
                        ticker box had the same fate.

                        Worth stating rather than folding into a shared helper:
                        the two lists differ — the add row has an Add button per
                        entry and this has one Apply for the family — and the
                        control set is small enough that a component taking six
                        props would hide more than it saved. */}
                    {spec.kind === "bool" ? (
                      <input
                        type="checkbox"
                        aria-label={`${row.label} ${spec.label}`}
                        checked={current !== "0"}
                        onChange={(event) => set(event.target.checked ? "1" : "0")}
                      />
                    ) : spec.kind === "choice" ? (
                      <select
                        aria-label={`${row.label} ${spec.label}`}
                        value={current}
                        onChange={(event) => set(event.target.value)}
                        style={PARAM_INPUT}
                      >
                        {(spec.choices ?? []).map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type={spec.kind === "symbol" ? "text" : "number"}
                        aria-label={`${row.label} ${spec.label}`}
                        value={current}
                        onChange={(event) =>
                          set(
                            spec.kind === "symbol"
                              ? event.target.value.toUpperCase()
                              : event.target.value,
                          )
                        }
                        style={PARAM_INPUT}
                      />
                    )}
                  </label>
                );
              })}
              <button
                type="button"
                aria-label={`Apply ${row.label} parameters`}
                onClick={apply}
                style={ADD}
              >
                Apply
              </button>
            </div>
          )}
          {!entry && (
            <div style={NOTE}>
              No catalog entry for this series, so its parameters cannot be
              re-read from the alias.
            </div>
          )}
          {/* **Weekly on a daily chart** (owner, 2026-11-02;
              `docs/plans/htf-indicators-and-levels.md` A): each day shows the
              weekly reading as of that day. Offered on a daily chart, for a
              family the catalog marks `higherTimeframe`. */}
          {onTimeframe && (entry?.higherTimeframe || row.timeframe) && (
            <div style={SCALE_ROW}>
              <span style={SCALE_LABEL}>Timeframe</span>
              {([undefined, "w", "m"] as const).map((tf) => (
                <button
                  key={tf ?? "chart"}
                  type="button"
                  aria-label={`${row.label} timeframe ${tf ? (tf === "w" ? "weekly" : "monthly") : "chart"}`}
                  aria-pressed={row.timeframe === tf}
                  onClick={() => row.timeframe !== tf && onTimeframe(tf)}
                  style={scaleChoiceStyle(row.timeframe === tf)}
                >
                  {tf === undefined ? "Chart" : tf === "w" ? "Weekly" : "Monthly"}
                </button>
              ))}
            </div>
          )}
          {paneScaleChoice && onPaneScaleChoice && (
            <div style={SCALE_ROW}>
              <span style={SCALE_LABEL}>Scale</span>
              <select
                aria-label={`${row.label} scale`}
                value={paneScaleChoice}
                onChange={(e) => onPaneScaleChoice(e.target.value as ScaleChoice)}
                style={SELECT}
              >
                {PANE_SCALES.map((choice) => (
                  <option key={choice.id} value={choice.id} title={choice.title}>
                    {choice.label}
                  </option>
                ))}
                {/* Merge onto another indicator's scale — two Ranks, one axis. */}
                {scalePartners.map((partner) => (
                  <option
                    key={partner.axisId}
                    value={`with:${partner.axisId}`}
                    title={`Draw on ${partner.label}'s scale`}
                  >
                    With {partner.label}
                  </option>
                ))}
              </select>
            </div>
          )}
          {onSmooth && (
            <SmoothRow label={row.label} action="Add" onSubmit={onSmooth} />
          )}
          {onEditSmoother && (
            <SmoothRow
              label={row.label}
              action="Apply"
              initial={splitSmoothed(row.aliases[0])}
              onSubmit={onEditSmoother}
            />
          )}
          {/* Its own scale in a shared pane can be fixed too (owner, 2026-10-06):
              a Rank pinned to 0–100 over the candles, or HJ to a range of
              your choosing. The pane's control below is for the pane's axis. */}
          {ownAxis && onOwnAxisScale && (
            <PaneScaleRow
              row={row}
              axis={ownAxis}
              label="Range"
              onScale={(scale) => onOwnAxisScale(ownAxis.id, scale)}
            />
          )}
          {/* **Only for an own-pane indicator.** An overlay draws on the price
              axis, and what that axis scales to is the chart-level `Price axis`
              setting — offering a second control for the same axis here would
              give two answers to one question. */}
          {onScale && row.placement === "own" && scaleIsPanes && (
            <PaneScaleRow
              row={row}
              axis={row.axis}
              label="Pane scale"
              onScale={(scale) => onScale(row.paneId, scale)}
            />
          )}
          {/* **The same gate, and the same reason.** An overlay's `axis` is the
              *price* axis — `appliedIndicators` hands it over so the row knows
              what it draws against — and a level placed on that one would be a
              line across the candles, which is a drawing rather than an
              indicator setting. `row.axis` on top of it, because a hand-built
              pane that declares no axis has nowhere to store the array. */}
          {row.placement === "own" && row.axis && (
            <AxisLevelsRow row={row} onLevels={onLevels} />
          )}

          {onPlot &&
            row.plots.map((plot) => (
              <PlotStyleRow
                key={plot.plotId}
                plot={plot}
                onChange={(patch) => onPlot(plot.plotId, patch)}
                // No ✕ on the last line: removing the whole family is the
                // row's own ×, and a family with no lines is a row of nothing.
                onRemove={
                  onRemoveOutput && row.plots.length > 1 ? () => onRemoveOutput(plot.plotId) : undefined
                }
              />
            ))}
          {onAddOutput && removed.length > 0 && (
            <div style={PLOT_ROW}>
              <span style={NOTE}>+ Add back:</span>
              {removed.map((output) => (
                <button
                  key={output.name}
                  type="button"
                  aria-label={`Add back ${output.label}`}
                  onClick={() => onAddOutput(output.name)}
                  style={scaleChoiceStyle(false)}
                >
                  {output.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * The style controls for one plot: type, width, dash, colour.
 *
 * Applied on change with no Apply button, because every one of them is a
 * repaint of data already loaded — see the header. Plot type is the exception in
 * cost but not in feel: it rebuilds, and making it the one control that needs
 * confirming would be a worse trade than the rebuild.
 */
function PlotStyleRow({
  plot,
  onChange,
  onRemove,
}: {
  plot: AppliedPlot;
  onChange: (patch: PlotPatch) => void;
  /** Delete this line; absent on a family's last one. */
  onRemove?: () => void;
}) {
  const resolveToken = useTokenResolver();
  const mode = colorModeOf(plot);
  const dashable = plot.type === "line" || plot.type === "mountain";
  const signable = SIGN_COLORABLE.has(plot.type);
  /**
   * A fill, rather than a series with a stroke — which changes three things
   * about this row: what the colour control edits, which controls are offered
   * at all, and whether there is a shape to choose.
   */
  const filled = plot.type === "band" || plot.type === "volumeByPrice";
  /**
   * Volume-by-Price is a fill for every purpose this row has — its colour is a
   * fill, it has no stroke to set, it has no shape to change to, and its opacity
   * is the number a person most wants to reach.
   *
   * **It is called out rather than folded silently into `filled`**, because two
   * of the three consequences below would otherwise be wrong for it and nobody
   * would find out from reading the flag. Its colours are the *paired* tokens,
   * like a candle's, so the single-colour listbox has nothing to edit — and a
   * control that writes a field the renderer ignores is worse than an absent
   * one. That was the Bollinger swatch bug in this same file, where the control
   * named a colour nothing was drawn in and picking it was the only way anyone
   * ever saw the fill.
   */
  const volumeByPrice = plot.type === "volumeByPrice";
  const candleDefault = useDefaultCandleStyle();
  /**
   * A signal and a fill both have no type to change *to*, which is why the
   * select disappears rather than being disabled with them added to the list.
   *
   * `PLOT_TYPES` already leaves out the shapes whose data an indicator output
   * cannot fill — candlestick, ohlc and band want more than one number a bar.
   * This is that rule from the other side. A signal's column is `0 / +1 / -1`
   * and its y is a lane index, so drawing it as a line is a line at zero with
   * occasional spikes to one; a band is fed two columns, so every option in the
   * list would draw one of them and silently discard the other. Both render,
   * which is what makes offering the choice worse than withholding it — and
   * because `band` is not in the list, the select was showing "Line" against a
   * plot that is not one, so picking anything at all was a change.
   */
  const fixedShape = plot.type === "signal" || filled;
  const signal = plot.type === "signal";
  /**
   * A paint bar (HCD/LCD) colours candle bodies and draws nothing of its own:
   * paired colours like a signal, and no width, dash or shape to offer.
   */
  const paint = plot.type === "paint";
  /**
   * A marker plot — a signal or a scatter — has a glyph to choose instead of a
   * dash, so the shape select takes the dash select's slot: markers were never
   * dashable (that select sat disabled on them), and reusing the slot keeps every
   * other control in the row where it was.
   */
  const shapeable = signal || plot.type === "scatter";
  /**
   * Which colour control this row offers, if any.
   *
   * A Volume-by-Price plot is coloured by its **paired** palette — up volume and
   * down volume, the same two tokens a candle uses — so the single-colour
   * listbox has nothing it could write. `PlotStyle.stroke` is read by nothing on
   * this path, so offering it would put a swatch on screen naming a colour that
   * is nowhere on the chart.
   */
  /**
   * **A signal is always paired**, so it gets the Up and Down swatches even
   * with no palette stored. The builder paints its markers with the up/down
   * colours — falling back to `positive`/`negative` — and never reads
   * `PlotStyle.stroke`, so the single swatch named a colour ("Series 1") that
   * appeared nowhere on the chart. Picking one did nothing, which is how it was
   * reported.
   */
  const singleColour = mode === "single" && !volumeByPrice && !signal && !paint;
  // A flag signal fires only 1, so only its up colour is ever drawn: one picker,
  // bound to that, rather than a down swatch that colours nothing.
  const flagColour = signal && plot.flag === true;

  /**
   * The single-colour token: the stroke, falling back to what a plot arrives
   * with.
   *
   * **A band is the exception, and it is the colour the user can actually
   * see.** A catalog fill is minted with a transparent stroke and zero width —
   * the fill *is* the plot — so editing `stroke` there changes nothing visible
   * and the swatch shows a colour that is not on the chart.
   */
  /**
   * **The fallback differs by kind, because the two are drawn differently.**
   *
   * A line with no stroke of its own gets `series1`. A *fill* with none does
   * not: `scichart-builder`'s band arm resolves `paired?.up ?? fill ?? stroke`,
   * and a catalog fill is minted with `stroke: "transparent"` — which
   * `resolveColorRef` answers `undefined` for — so it lands on `pairedColors`'
   * first element, the `positive` token. Showing `series1` there was a swatch
   * claiming a colour that is neither in the model nor on the chart.
   *
   * It made the control actively misleading rather than merely wrong: picking
   * the value already displayed *wrote* it, which both recoloured the band and
   * — before the double-alpha fix — was the only way anyone ever saw the fill
   * at all. "Select what it already says" must be a no-op.
   */
  const stroke =
    (filled ? plot.style?.fill : plot.style?.stroke) ?? (filled ? "positive" : "series1");
  /**
   * **Volume-by-Price falls back to grey, because that is what it draws.**
   *
   * A fresh VBP plot carries no palette — its catalog output has no
   * `colorMode`, so `add-indicator` mints none — and `paintVolumeByPrice` then
   * uses its own `neutral`/`axis` pair rather than the candles'. Showing
   * `positive`/`negative` here was the defect this block's neighbour above
   * records: a swatch claiming a colour that is nowhere on the chart, and
   * picking the displayed value *wrote* a palette, which flipped the zones from
   * grey to green and red. "Select what it already says" must be a no-op.
   */
  const up = plot.palette?.upColor ?? (volumeByPrice ? "neutral" : paint ? "accent.orange" : "positive");
  const down = plot.palette?.downColor ?? (volumeByPrice ? "axis" : paint ? "accent.blue" : "negative");

  /**
   * Switching colour mode has to *clear* the other tier, not just add to it.
   *
   * `undefined` reaches `setPlotPalette`, which deletes the key rather than
   * storing it — otherwise a plot switched back to one colour keeps a
   * `colorMode` the render plan still reads, and paints as neither choice.
   */
  const setMode = (next: ColorMode) => {
    if (next === "single") {
      onChange({
        palette: {
          upColor: undefined,
          downColor: undefined,
          upFadeColor: undefined,
          downFadeColor: undefined,
          colorMode: undefined,
        },
      });
      return;
    }
    // Into ×4 from anything else: the reference app's four colours, not the
    // two the plot had plus two guesses (owner, 2026-10-06).
    if (next === "quad") {
      onChange({ palette: mode === "quad" ? { ...plot.palette, colorMode: "quad" } : { ...QUAD_DEFAULTS } });
      return;
    }
    onChange({
      palette: { upColor: up, downColor: down, upFadeColor: undefined, downFadeColor: undefined, colorMode: next },
    });
  };

  /**
   * **A bar-highlight indicator** (Inside / Outside Bars): its two colours and
   * how transparent the boxes are, and nothing else (owner, 2026-10-13). It
   * draws boxes behind candles, so a type, a width or a dash would be controls
   * that change nothing. Inside is `palette.upColor`, outside `downColor`.
   */
  if (isBarHighlightAlias(plot.alias)) {
    const opacity = plot.style?.opacity ?? DEFAULT_OPACITY;
    return (
      <div style={PLOT_ROW}>
        <button
          type="button"
          aria-label={`${plot.label} ${plot.hidden ? "show" : "hide"}`}
          aria-pressed={!plot.hidden}
          title={plot.hidden ? "Hidden — click to show" : "Shown — click to hide"}
          onClick={() => onChange({ hidden: !plot.hidden })}
          style={{ ...EYE_BUTTON, opacity: plot.hidden ? 0.45 : 1 }}
        >
          <EyeIcon off={plot.hidden ?? false} />
        </button>
        <span style={SCALE_LABEL}>Inside</span>
        <ColorSelect
          label="Inside bar colour"
          value={plot.palette?.upColor ?? INSIDE_COLOR}
          resolve={resolveToken}
          onChange={(token) => onChange({ palette: { ...plot.palette, upColor: token } })}
        />
        <span style={SCALE_LABEL}>Outside</span>
        <ColorSelect
          label="Outside bar colour"
          value={plot.palette?.downColor ?? OUTSIDE_COLOR}
          resolve={resolveToken}
          onChange={(token) => onChange({ palette: { ...plot.palette, downColor: token } })}
        />
        <span style={SCALE_LABEL}>Opacity %</span>
        <input
          type="number"
          min={0}
          max={100}
          step={5}
          aria-label="Highlight opacity percent"
          title="How strong the boxes are: 0 invisible, 100 solid"
          value={Math.round(opacity * 100)}
          onChange={(e) => {
            const value = Number(e.target.value);
            if (!Number.isFinite(value) || e.target.value.trim() === "") return;
            onChange({ style: { ...plot.style, opacity: Math.min(100, Math.max(0, value)) / 100 } });
          }}
          style={WIDTH_INPUT}
        />
      </div>
    );
  }

  return (
    <div style={PLOT_ROW}>
      <button
        type="button"
        aria-label={`${plot.label} ${plot.hidden ? "show" : "hide"}`}
        aria-pressed={!plot.hidden}
        title={plot.hidden ? "Hidden — click to show" : "Shown — click to hide"}
        onClick={() => onChange({ hidden: !plot.hidden })}
        style={{ ...EYE_BUTTON, opacity: plot.hidden ? 0.45 : 1 }}
      >
        <EyeIcon off={plot.hidden ?? false} />
      </button>
      <span
        style={{
          ...PLOT_LABEL,
          // Struck through rather than only dimmed. Dimming alone is what the
          // hover state already does, so on a row the pointer is over, "hidden"
          // and "hovered" would look the same.
          textDecoration: plot.hidden ? "line-through" : undefined,
          opacity: plot.hidden ? 0.6 : 1,
        }}
        title={plot.alias}
      >
        {plot.label}
      </span>

      {plot.paintable ? (
        // The same column two ways (owner, 2026-10-05): colouring the candle
        // bodies, or as markers on a lane. In the type slot, because it *is*
        // the plot's type and the row keeps its layout.
        <select
          aria-label={`${plot.label} draw as`}
          value={paint ? "paint" : "signal"}
          onChange={(e) => onChange({ type: e.target.value as PlotType })}
          style={SELECT}
        >
          <option value="paint">Paint candles</option>
          <option value="signal">Markers</option>
        </select>
      ) : fixedShape || paint ? (
        // The slot still has to be filled, or every control to the right of it
        // shifts left on the one row that has no type select.
        <span
          style={SELECT_PLACEHOLDER}
          title={
            filled
              ? "A fill is the shape between two series"
              : "A signal is always drawn as markers"
          }
        >
          {filled ? "Fill" : "Markers"}
        </span>
      ) : (
        <select
          aria-label={`${plot.label} type`}
          value={plot.type}
          onChange={(e) => onChange({ type: e.target.value as PlotType })}
          style={SELECT}
        >
          {PLOT_TYPES.map((t) => (
            <option key={t.id} value={t.id}>
              {t.label}
            </option>
          ))}
        </select>
      )}

      {/* A fill has neither. Its stroke is transparent at zero width, so a
          width box and a dash select on that row are two controls that read as
          available and do nothing at all — worse than absent, because the only
          way to find out is to try one. What a fill has is a colour. */}
      {/* On a marker row the same box is the marker's SIZE. A filled marker's
          outline is drawn in its own colour, so its width was a control with no
          visible effect; size is what a person reaching for this box wants, and
          reusing the slot keeps the row's layout (the panel is a fixed width). */}
      {shapeable && (
      <input
        type="number"
        min={2}
        max={40}
        step={1}
        aria-label={`${plot.label} marker size`}
        // A column's size is the bar's width and the lane's height, so the box
        // stays in place (the row's layout does not move) but greys out. The
        // stored size is kept for when the shape goes back to a glyph.
        disabled={signal && plot.style?.markerShape === "column"}
        title={
          signal && plot.style?.markerShape === "column"
            ? "A column fills its lane, so it has no size"
            : "Marker size in pixels"
        }
        value={plot.style?.markerSize ?? ""}
        placeholder={String(signal ? SIGNAL_MARKER_PX : SCATTER_MARKER_PX)}
        onChange={(e) => {
          const raw = e.target.value.trim();
          const value = Number(raw);
          onChange({
            style: {
              ...plot.style,
              // Blank is the renderer's default for the type, not zero.
              markerSize:
                raw === "" || !Number.isFinite(value)
                  ? undefined
                  : Math.min(40, Math.max(2, value)),
            },
          });
        }}
        style={WIDTH_INPUT}
      />
      )}

      {!filled && !shapeable && !paint && (
      <input
        type="number"
        min={0}
        step={0.5}
        aria-label={`${plot.label} width`}
        title="Stroke width in pixels"
        value={plot.style?.thickness ?? ""}
        placeholder="auto"
        onChange={(e) => {
          const value = Number(e.target.value);
          // Blank means "whatever this shape's default is", which is not the
          // same as zero — zero is a column with no outline, and a user who
          // clears the box has not asked for that.
          onChange({
            style: {
              ...plot.style,
              thickness:
                e.target.value.trim() === "" || !Number.isFinite(value) ? undefined : value,
            },
          });
        }}
        style={WIDTH_INPUT}
      />
      )}

      {/**
       * A fill's opacity, as a **percentage**.
       *
       * Fills only, and the row has the space precisely because it is a fill:
       * a band's shape is fixed so there is no type select, and a band cannot
       * be dashed so there is no dash select. This sits where those two are
       * not, which is why it costs no width — the panel is a fixed fraction and
       * a control added to one is a sizing change, not a free addition.
       *
       * **Percent in the control, fraction in the contract.** `PlotStyle.opacity`
       * is 0–1 and stays that way; nobody types `0.12` when they mean twelve
       * percent. The conversion is one place, here, so nothing downstream has to
       * know the control exists.
       *
       * It is a repaint, never a rebuild: `opacity` is excluded from
       * `structuralKey`, so this costs no WebGL teardown and no refetch — the
       * same reason the width spinner beside it can fire per keystroke.
       *
       * Blank restores the catalog's own value rather than meaning zero, which
       * is the width input's rule and for its reason: a user clearing a box has
       * not asked for an invisible band, and zero is exactly what they had
       * before this control existed.
       */}
      {filled && (
      <input
        type="number"
        min={0}
        max={100}
        step={1}
        aria-label={`${plot.label} opacity percent`}
        title="Fill opacity, percent"
        value={plot.style?.opacity === undefined ? "" : Math.round(plot.style.opacity * 100)}
        placeholder="%"
        onChange={(e) => {
          const raw = e.target.value.trim();
          const value = Number(raw);
          const blank = raw === "" || !Number.isFinite(value);
          onChange({
            style: {
              ...plot.style,
              // Clamped rather than refused: the spinner's own arrows stop at
              // the bounds, but a typed 250 has to land somewhere and a band
              // drawn at 2.5x alpha is not a thing SciChart can mean.
              opacity: blank ? undefined : Math.min(100, Math.max(0, value)) / 100,
            },
          });
        }}
        style={WIDTH_INPUT}
      />
      )}

      {shapeable && (
        <select
          aria-label={`${plot.label} marker shape`}
          title="Marker shape"
          value={plot.style?.markerShape ?? DEFAULT_MARKER_SHAPE}
          onChange={(e) =>
            onChange({
              style: { ...plot.style, markerShape: e.target.value as MarkerShape },
            })
          }
          style={SELECT}
        >
          {(signal ? MARKER_SHAPES : SCATTER_SHAPES).map((shape) => (
            <option key={shape} value={shape}>
              {MARKER_SHAPE_LABELS[shape]}
            </option>
          ))}
        </select>
      )}

      {/* The words a `text` marker draws (owner, 2026-10-28): typed here,
          applied per keystroke — a repaint of a sprite, so nothing to batch.
          Blank keeps the last text rather than drawing nothing. */}
      {shapeable && plot.style?.markerShape === "text" && (
        <MarkerTextInput
          label={plot.label}
          text={plot.style?.markerText ?? DEFAULT_MARKER_TEXT}
          onText={(text) => onChange({ style: { ...plot.style, markerText: text } })}
        />
      )}

      {!filled && !shapeable && !paint && (
      <select
        aria-label={`${plot.label} dash`}
        // Only the stroked-line family can draw one; SciChart has no dash for a
        // column or a marker, so the control says so rather than doing nothing.
        disabled={!dashable}
        title={dashable ? "Dash pattern" : "Only a line or mountain can be dashed"}
        value={plot.style?.dashStyle ?? "solid"}
        onChange={(e) =>
          onChange({
            style: {
              ...plot.style,
              dashStyle: e.target.value as NonNullable<PlotStyle["dashStyle"]>,
            },
          })
        }
        style={SELECT}
      >
        {DASH_STYLES.map((d) => (
          <option key={d.id} value={d.id}>
            {d.label}
          </option>
        ))}
      </select>
      )}

      {signable && (
        <select
          aria-label={`${plot.label} colour mode`}
          value={mode}
          onChange={(e) => setMode(e.target.value as ColorMode)}
          style={SELECT}
        >
          {COLOR_MODES.map((m) => (
            <option key={m.id} value={m.id}>
              {m.label}
            </option>
          ))}
        </select>
      )}

      {singleColour ? (
        <ColorSelect
          label={`${plot.label} colour`}
          value={stroke}
          resolve={resolveToken}
          onChange={(token) =>
            onChange({
              style: {
                ...plot.style,
                ...(filled ? { fill: token } : { stroke: token }),
              },
            })
          }
        />
      ) : flagColour ? (
        <ColorSelect
          label={`${plot.label} colour`}
          value={up}
          resolve={resolveToken}
          onChange={(token) => onChange({ palette: { ...plot.palette, upColor: token } })}
        />
      ) : (
        <>
          <ColorSelect
            label={`${plot.label} up colour`}
            value={up}
            resolve={resolveToken}
            onChange={(token) => onChange({ palette: { ...plot.palette, upColor: token } })}
          />
          <ColorSelect
            label={`${plot.label} down colour`}
            value={down}
            resolve={resolveToken}
            onChange={(token) => onChange({ palette: { ...plot.palette, downColor: token } })}
          />
          {/* ×4: the fading pair — above zero and falling, below and rising. */}
          {mode === "quad" && (
            <>
              <ColorSelect
                label={`${plot.label} up fading colour`}
                value={plot.palette?.upFadeColor ?? QUAD_DEFAULTS.upFadeColor}
                resolve={resolveToken}
                onChange={(token) => onChange({ palette: { ...plot.palette, upFadeColor: token } })}
              />
              <ColorSelect
                label={`${plot.label} down fading colour`}
                value={plot.palette?.downFadeColor ?? QUAD_DEFAULTS.downFadeColor}
                resolve={resolveToken}
                onChange={(token) => onChange({ palette: { ...plot.palette, downFadeColor: token } })}
              />
            </>
          )}
        </>
      )}

      {/* Hollow candles (owner, 2026-10-05; `plan/candle-colors.ts`). Shows the
          viewer's default — hollow for a JAS member — until a pick is made, and
          the pick is what gets stored. */}
      {plot.type === "candlestick" && (
        <select
          aria-label={`${plot.label} candle colouring`}
          title="Close vs open: up when the close is above the open. Hollow: the outline follows the previous close, and the body is empty on an up day."
          value={plot.palette?.candleStyle ?? candleDefault}
          onChange={(e) =>
            onChange({ palette: { ...plot.palette, candleStyle: e.target.value as CandleStyle } })
          }
          style={SELECT}
        >
          <option value="standard">Close vs open</option>
          <option value="hollow">Hollow</option>
        </select>
      )}

      {onRemove && (
        <button
          type="button"
          aria-label={`Delete ${plot.label}`}
          title="Delete this line"
          onClick={onRemove}
          style={{ ...REMOVE, marginLeft: "auto" }}
        >
          ✕
        </button>
      )}
    </div>
  );
}

/**
 * The `fixed` popover the rest of the app uses, opening to the left of its
 * trigger — see the header for why neither of those is a preference.
 *
 * Escape lives here rather than on the panel so it works wherever focus is,
 * including a parameter input.
 */
function Popover({
  anchorRef,
  labelledBy,
  children,
  onKeyDown,
  onClose,
}: {
  anchorRef: React.RefObject<HTMLElement | null>;
  labelledBy: string;
  children: React.ReactNode;
  onKeyDown: (event: React.KeyboardEvent) => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    const panel = ref.current;
    if (!anchor || !panel) return;

    const rect = anchor.getBoundingClientRect();
    const height = panel.offsetHeight;
    const width = panel.offsetWidth;

    // Top-aligned with the button and clamped into the window, rather than hung
    // below it: this panel is most of the viewport tall, so "below" would put
    // nearly all of it off-screen.
    const top = Math.max(4, Math.min(rect.top, window.innerHeight - height - 4));
    const left = Math.max(4, Math.min(rect.left - width - 6, window.innerWidth - width - 4));
    setPos({ top, left });
  }, [anchorRef]);

  useEffect(() => {
    // A layer of this panel drawn outside it — the Move to menu is portalled to
    // the body so the scrolling list cannot clip it — is still this panel: a
    // press or a scroll there is not "outside".
    const inLayer = (node: Node) => {
      const el = node instanceof Element ? node : node.parentElement;
      return Boolean(el?.closest("[data-qf-picker-layer]"));
    };
    const onDown = (event: PointerEvent) => {
      const target = event.target as Node;
      // A pointerdown on the trigger is not an outside click: closing here and
      // letting the button's own click reopen it is why a panel cannot be shut
      // by the button that opened it.
      if (ref.current?.contains(target) || anchorRef.current?.contains(target)) return;
      if (inLayer(target)) return;
      onClose();
    };
    document.addEventListener("pointerdown", onDown, true);

    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      onClose();
      // Focus goes back where it came from, or the next Tab starts at the top
      // of the document.
      anchorRef.current?.focus();
    };
    document.addEventListener("keydown", onKey);

    // A fixed element does not travel with a scrolling ancestor, so it would
    // detach from its trigger. Closing is honest and cheaper than tracking.
    //
    // **The listener has to be capturing, and that is what made it fire on the
    // panel's own list.** `scroll` does not bubble, so a non-capturing listener
    // on `window` never hears an ancestor `div` scroll at all — capture is the
    // only way to catch the case this defends against. But capture hears
    // *every* scroll in the document, including the one inside this panel: the
    // wheel over the indicator list, and dragging its scrollbar, both closed
    // the panel the moment it moved. Reading the list was impossible.
    //
    // So the phase stays and the origin is checked. A scroll inside the panel
    // moves nothing the panel is positioned against; only an ancestor's does.
    // (`Node.contains` is true for the node itself, so a scroll of the panel
    // element is covered as well as one of anything nested in it.)
    const onScroll = (event: Event) => {
      if (ref.current?.contains(event.target as Node)) return;
      if (event.target instanceof Node && inLayer(event.target)) return;
      onClose();
    };
    const onResize = () => onClose();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      document.removeEventListener("pointerdown", onDown, true);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [anchorRef, onClose]);

  return (
    <div
      ref={ref}
      role="dialog"
      aria-labelledby={labelledBy}
      // `data-tour`: labelled by an id rather than a literal, so there is no
      // stable attribute selector without this. See lib/tour.ts.
      data-tour="indicator-picker"
      onKeyDown={onKeyDown}
      style={{
        position: "fixed",
        // Placed off the first frame's measurement; `visibility` rather than
        // `display`, which would have no size to measure.
        top: pos?.top ?? 0,
        left: pos?.left ?? 0,
        visibility: pos ? "visible" : "hidden",
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
        // Counted against the widest ordinary row, and the target is that a plot
        // fits on **one line**: a wrapped row reads as two plots, which on a
        // family of six is a panel you have to decode rather than scan.
        //
        // The sequence is the eye (16), the label (72), the type select, the
        // width box (48), the dash select and the colour swatch plus its select.
        // The selects size to their own text rather than to a fixed width —
        // "Scatter", "Negative" — and measured on screen they run ~105 each, not
        // the ~90 estimated at 520, which is why that still wrapped. 600 clears
        // it with room for the colour-mode select a sign-coloured column adds.
        //
        // A *paired* palette is two colour selects and still wraps. That is left
        // alone deliberately: it is MACD's histogram and little else, and sizing
        // every panel to the widest rare row makes the common one worse.
        //
        // `positionPanel` measures `offsetWidth` and clamps to the viewport, so
        // widening cannot push it off-screen; on a narrow window it moves left
        // rather than overflowing.
        width: 600,
        maxHeight: "78vh",
        background: "var(--qf-surface)",
        border: "1px solid var(--qf-border)",
        borderRadius: 6,
        boxShadow: "0 6px 20px rgba(0,0,0,0.35)",
        color: "var(--qf-text)",
        textAlign: "left",
      }}
    >
      {children}
    </div>
  );
}

const HEADER: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "0.5rem 0.7rem",
  borderBottom: "1px solid var(--qf-border)",
};

const TITLE: React.CSSProperties = {
  margin: 0,
  font: "inherit",
  fontSize: 13,
  fontWeight: 600,
};

const CLOSE: React.CSSProperties = {
  font: "inherit",
  fontSize: "1.1rem",
  lineHeight: 1,
  padding: "0 0.35rem",
  border: 0,
  borderRadius: 4,
  background: "transparent",
  color: "var(--qf-text-secondary)",
  cursor: "pointer",
};

const SEARCH: React.CSSProperties = {
  margin: "0.6rem 0.7rem",
  padding: "0.4rem 0.5rem",
  font: "inherit",
  fontSize: 12,
  background: "var(--qf-surface-alt)",
  color: "var(--qf-text)",
  border: "1px solid var(--qf-border)",
  borderRadius: 5,
  outline: "none",
};

const SECTION: React.CSSProperties = {
  padding: "0 0.7rem 0.5rem",
  borderBottom: "1px solid var(--qf-border)",
};

const SECTION_LABEL: React.CSSProperties = {
  fontSize: 10,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "var(--qf-text-muted)",
  padding: "2px 0 6px",
};

/** A section header that opens and closes it — `SECTION_LABEL`'s look, as a button. */
const SECTION_TOGGLE: React.CSSProperties = {
  ...SECTION_LABEL,
  display: "block",
  width: "100%",
  textAlign: "left",
  background: "none",
  border: "none",
  cursor: "pointer",
  font: "inherit",
  fontSize: 10,
};

/**
 * "On this chart" scrolls inside itself past about two-fifths of the viewport.
 * `vh` rather than a percentage of the dialog: the dialog's height is a
 * max-height, which a child's percentage cannot resolve against.
 */
const APPLIED_SCROLL: React.CSSProperties = {
  maxHeight: "38vh",
  overflowY: "auto",
};

/** An opened Fill between is capped too, so it cannot take the catalog's 30%. */
const FILL_SCROLL: React.CSSProperties = {
  maxHeight: "22vh",
  overflowY: "auto",
};

/**
 * The reorder grip: drag it, or focus it and press ↑/↓.
 *
 * A real `<button>` rather than a styled `<div>`, and that is the accessible
 * half of the feature rather than a detail — the on-chart arrows only exist
 * under a hovering pointer, so this is the one reorder control a keyboard can
 * reach. Arrow keys move one slot and are swallowed, because the popover's own
 * key handler drives the catalog list's cursor with the same two keys.
 *
 * `touchAction: "none"` is what makes the drag work on a trackpad-less device:
 * without it the browser claims the gesture for scrolling before the first
 * `pointermove` arrives.
 */
function GripHandle({
  label,
  canMoveUp,
  canMoveDown,
  onDragStart,
  onNudge,
}: {
  label: string;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onDragStart: (event: ReactPointerEvent<HTMLElement>) => void;
  onNudge: (delta: -1 | 1) => void;
}) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      type="button"
      // Says what it does *and* how, because a grip that is also a keyboard
      // control has no glyph for the second half.
      aria-label={`Reorder ${label}: drag, or use the arrow keys`}
      title="Drag to reorder — or focus and press ↑ / ↓"
      onPointerDown={onDragStart}
      // The capture that starts on `pointerdown` retargets the click that
      // follows, so without this a drag that ends over the row would also fire
      // whatever the row's own click does.
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => {
        if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;
        const delta = event.key === "ArrowUp" ? -1 : 1;
        // Swallowed even when the move is refused: letting it bubble would move
        // the catalog list's cursor instead, which is the surprise.
        event.preventDefault();
        event.stopPropagation();
        if (delta === -1 ? canMoveUp : canMoveDown) onNudge(delta);
      }}
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
      style={GRIP(hovered)}
    >
      ⠿
    </button>
  );
}

const GRIP = (hovered: boolean): React.CSSProperties => ({
  flex: "0 0 auto",
  border: "none",
  background: "transparent",
  padding: "0 2px",
  font: "inherit",
  fontSize: 13,
  lineHeight: 1,
  // `grab`, not `move`: the row goes one way only, and the cursor is the first
  // thing that says so.
  cursor: "grab",
  // The whole gesture is a drag, so the browser must not read it as a scroll.
  touchAction: "none",
  color: hovered ? "var(--qf-text)" : "var(--qf-text-muted)",
  // Quiet at rest — there is one on every own-pane row, and a column of dark
  // glyphs down the left of the list would out-weigh the names beside them.
  opacity: hovered ? 1 : 0.55,
  transition: "opacity 120ms, color 120ms",
});

const APPLIED_ROW: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  fontSize: 12,
  padding: "2px 0",
};

const APPLIED_LABEL: React.CSSProperties = {
  flex: "1 1 auto",
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const FILL_ROW: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  fontSize: 12,
  padding: "3px 0",
};

const FILL_PANE: React.CSSProperties = {
  flex: "0 0 auto",
  fontSize: 10,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  color: "var(--qf-text-muted)",
  minWidth: 42,
};

const FILL_AND: React.CSSProperties = {
  flex: "0 0 auto",
  color: "var(--qf-text-muted)",
};

const FILL_ADD: React.CSSProperties = {
  flex: "0 0 auto",
  font: "inherit",
  fontSize: 11,
  padding: "2px 8px",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  background: "var(--qf-surface-alt)",
  color: "var(--qf-text)",
  cursor: "pointer",
};

const REMOVE: React.CSSProperties = {
  flex: "0 0 auto",
  font: "inherit",
  fontSize: "1rem",
  lineHeight: 1,
  padding: "0 6px",
  border: 0,
  borderRadius: 4,
  background: "transparent",
  color: "var(--qf-text-secondary)",
  cursor: "pointer",
};

/** The expanded editor, indented under its row so the list still reads as a list. */
const EDITOR: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 4,
  margin: "2px 0 8px",
  padding: "6px 8px",
  borderRadius: 4,
  border: "1px solid var(--qf-border)",
  background: "var(--qf-surface-alt)",
};

const NOTE: React.CSSProperties = {
  fontSize: 11,
  lineHeight: 1.4,
  color: "var(--qf-text-muted)",
};

const PLOT_ROW: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: 4,
  fontSize: 11,
};

/**
 * The show/hide toggle, first in the row.
 *
 * 16px plus the row's 4px gap. It was paid for out of `PLOT_LABEL` while the
 * panel was 420 wide, because a control appended to a full row wraps the colour
 * swatch onto a second line; the panel is 520 now, so the label has its width
 * back and "Short Stop" fits without an ellipsis.
 */
const EYE_BUTTON: React.CSSProperties = {
  flex: "0 0 auto",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 16,
  height: 16,
  padding: 0,
  border: "none",
  background: "none",
  color: "var(--qf-text-secondary)",
  cursor: "pointer",
};

/** An open eye, or one with a line through it. */
function EyeIcon({ off }: { off: boolean }) {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M1 8s2.6-4.2 7-4.2S15 8 15 8s-2.6 4.2-7 4.2S1 8 1 8Z" />
      <circle cx="8" cy="8" r="1.8" />
      {off && <line x1="2.4" y1="13.6" x2="13.6" y2="2.4" />}
    </svg>
  );
}

const PLOT_LABEL: React.CSSProperties = {
  flex: "0 0 4.5rem",
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  color: "var(--qf-text-secondary)",
};

const SELECT: React.CSSProperties = {
  font: "inherit",
  fontSize: 11,
  padding: "2px 3px",
  background: "var(--qf-surface)",
  color: "var(--qf-text)",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  outline: "none",
};

/**
 * Where a type select would be, on a row that has no choice to offer.
 *
 * Borderless and dimmed so it reads as a statement rather than as a control
 * someone has disabled — the row is not missing a feature, the shape is simply
 * not a variable for this plot.
 */
const SELECT_PLACEHOLDER: React.CSSProperties = {
  font: "inherit",
  fontSize: 11,
  padding: "2px 3px",
  color: "var(--qf-text-muted)",
  border: "1px solid transparent",
};

/**
 * The text box for a `text` marker. Its own state, so emptying it to retype is
 * allowed while the chart keeps the last non-blank text — a marker with no text
 * would be an invisible point.
 */
function MarkerTextInput({
  label,
  text,
  onText,
}: {
  label: string;
  text: string;
  onText: (text: string) => void;
}) {
  const [draft, setDraft] = useState(text);
  useEffect(() => setDraft(text), [text]);
  return (
    <input
      type="text"
      aria-label={`${label} marker text`}
      title={`Marker text, up to ${MARKER_TEXT_MAX} characters`}
      maxLength={MARKER_TEXT_MAX}
      value={draft}
      onChange={(e) => {
        setDraft(e.target.value);
        const trimmed = e.target.value.trim();
        if (trimmed && trimmed !== text) onText(trimmed);
      }}
      onBlur={() => setDraft(text)}
      style={{ ...WIDTH_INPUT, width: 56 }}
    />
  );
}

const WIDTH_INPUT: React.CSSProperties = {
  width: 48,
  font: "inherit",
  fontSize: 11,
  padding: "2px 4px",
  background: "var(--qf-surface)",
  color: "var(--qf-text)",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  outline: "none",
};

const LIST: React.CSSProperties = {
  flex: "1 1 auto",
  // **At least 30% of the dialog** (owner, 2026-09-30) — the dialog is 78vh at
  // most, so 24vh. Whatever is on the chart above it scrolls instead.
  minHeight: "24vh",
  overflowY: "auto",
  padding: "0 0.7rem 0.6rem",
};

const CATEGORY: React.CSSProperties = {
  margin: 0,
  font: "inherit",
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "var(--qf-text-muted)",
  padding: "10px 0 4px",
};

/** Short name, then the sentence, then the placement marker — as in the reference. */
function rowStyle(isActive: boolean, locked = false): React.CSSProperties {
  return {
    // Dimmed, not hidden, and still readable — the point of showing it is that
    // somebody can tell what they would be getting.
    opacity: locked ? 0.45 : 1,
    display: "grid",
    gridTemplateColumns: "4.5rem 1fr auto",
    alignItems: "baseline",
    gap: 8,
    width: "100%",
    font: "inherit",
    fontSize: 12,
    padding: "5px 6px",
    border: 0,
    borderRadius: 4,
    background: isActive ? "var(--qf-selection)" : "transparent",
    color: "var(--qf-text)",
    cursor: locked ? "not-allowed" : "pointer",
    textAlign: "left",
  };
}

const ROW_LABEL: React.CSSProperties = {
  fontWeight: 600,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const ROW_DESC: React.CSSProperties = {
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  color: "var(--qf-text-secondary)",
};

/**
 * Whether this indicator draws **events** rather than a continuous series.
 *
 * Read off the outputs' `plot` rather than kept as a flag on the entry, because
 * that is where the fact already lives — `signal` is what routes a plot to the
 * marker strip in the first place, so a second declaration could disagree with
 * the thing that decides.
 */
function isEventEntry(entry: IndicatorEntry): boolean {
  return entry.outputs.some((output) => output.plot === "signal");
}

const PILL: React.CSSProperties = {
  flex: "0 0 auto",
  fontSize: 10,
  padding: "1px 5px",
  border: "1px solid var(--qf-border)",
  borderRadius: 999,
  color: "var(--qf-text-muted)",
  whiteSpace: "nowrap",
};

/**
 * The event tag, in the accent rather than in the muted grey `PILL` uses.
 *
 * It says something about *what you will get*, where "own pane" says where it
 * goes — so it has to be the one a scanning eye stops on. Bordered rather than
 * filled, so a list with several of them does not read as a warning.
 */
const EVENT_PILL: React.CSSProperties = {
  ...PILL,
  color: "var(--qf-accent)",
  borderColor: "var(--qf-accent)",
};

/** Says why the row is dimmed. The row itself is already unclickable. */
const LOCK_PILL: React.CSSProperties = {
  ...PILL,
  color: "var(--qf-text-muted)",
  borderColor: "var(--qf-border)",
};

const PARAMS: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "flex-end",
  gap: 8,
  padding: "4px 6px 8px",
};

const PARAM: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 2,
  fontSize: 10,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "var(--qf-text-muted)",
};

const SCALE_ROW: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  flexWrap: "wrap",
  marginBottom: 6,
};

const SCALE_LABEL: React.CSSProperties = {
  fontSize: 11,
  color: "var(--qf-text-secondary)",
  letterSpacing: "0.02em",
};

/**
 * A chosen scale is an accent fill with background-coloured text, never accent
 * text — the accent is a light blue on the dark themes and a dark blue on White,
 * so coloured text would be unreadable on one of them. Same rule as `GroupBar`.
 */
function scaleChoiceStyle(on: boolean): React.CSSProperties {
  return {
    padding: "2px 8px",
    font: "inherit",
    fontSize: 12,
    borderRadius: 4,
    cursor: "pointer",
    border: `1px solid ${on ? "var(--qf-accent)" : "var(--qf-border)"}`,
    background: on ? "var(--qf-accent)" : "var(--qf-surface-alt)",
    color: on ? "var(--qf-background)" : "var(--qf-text)",
  };
}

/**
 * The levels editor, as one block under the Pane scale row.
 *
 * Its own container rather than more children of `EDITOR` so the header row and
 * the lines under it stay together at a tighter gap than the editor's own 4 —
 * they are one control, and at the editor's spacing the lines read as four
 * separate settings.
 */
const LEVELS_BLOCK: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 3,
  marginBottom: 6,
};

/**
 * One line's controls, and they are **counted** rather than left to wrap.
 *
 * The editor's content box is 557px: the 600 panel, less its 1px borders, less
 * `SECTION`'s 0.7rem sides, less `EDITOR`'s 8px sides and 1px border. A row
 * spends 66 on the value, 96 on the label, ~66 on the dash select, ~87 on the
 * colour trigger and ~20 on the ×, plus four 4px gaps — about 351, so it fits
 * on one line with room to spare and the panel keeps its width.
 *
 * That margin is the point. A wrapped row reads as two lines rather than one,
 * which on CCI's five is a block you decode instead of scan — the same rule the
 * panel's own width comment states for a plot's style row. There is no
 * `overflow` anywhere in this block on purpose: a scroller here would hide the
 * fifth line of a set whose whole job is to be read at once.
 */
/** A level's Fill line, indented under its value so the pair reads as one level. */
const LEVEL_FILL_ROW: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 4,
  fontSize: 11,
  margin: "3px 0 6px 12px",
};

const LEVEL_ROW: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 4,
  fontSize: 11,
};

/**
 * Narrower than a parameter box, because a level's label is a tag rather than a
 * sentence — "OB", "OS", "extreme". Wide enough for about ten characters, which
 * is where the row's spare width runs out.
 */
const LEVEL_LABEL_INPUT: React.CSSProperties = {
  width: 96,
  font: "inherit",
  fontSize: 11,
  padding: "3px 5px",
  background: "var(--qf-surface-alt)",
  color: "var(--qf-text)",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  outline: "none",
};

const PARAM_INPUT: React.CSSProperties = {
  width: 66,
  font: "inherit",
  fontSize: 12,
  padding: "3px 5px",
  background: "var(--qf-surface-alt)",
  color: "var(--qf-text)",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  outline: "none",
};

const ADD: React.CSSProperties = {
  marginLeft: "auto",
  font: "inherit",
  fontSize: 11,
  padding: "4px 12px",
  borderWidth: 1,
  borderStyle: "solid",
  borderColor: "var(--qf-accent)",
  borderRadius: 4,
  background: "var(--qf-surface-alt)",
  color: "var(--qf-accent)",
  cursor: "pointer",
};

const EMPTY: React.CSSProperties = {
  padding: "1rem 0.2rem",
  textAlign: "center",
  fontSize: 11,
  color: "var(--qf-text-muted)",
};
